// Translations. Flat map of key → [AU, 中文, Fil], ported from the design spec.
//
// Only UI chrome is translated. Domain content (pod/MO/quality-point/defect/
// team/document text) stays English and is translated downstream — it arrives
// from Odoo as data, never as a translation key.

export const LANGS = ['AU', '中文', 'Fil'] as const;
export type Lang = (typeof LANGS)[number];

export const TR = {
  // ── from the design spec ────────────────────────────────────────────────
  scanPod: ['Scan a pod', '扫描舱体', 'Mag-scan ng pod'],
  scanning: ['Scanning…', '扫描中…', 'Sini-scan…'],
  findPod: ['Find a pod', '查找舱体', 'Maghanap ng pod'],
  notHereYet: ['Not here yet', '尚未到达', 'Wala pa rito'],
  waitingOn: ['Waiting on', '正在等待', 'Naghihintay ng'],
  waitingOnOne: ['One step before it reaches you.', '还有一道工序才到你这里。', 'Isang hakbang bago ito dumating sa iyo.'],
  waitingOnMany: ['steps before it reaches you.', '道工序才到你这里。', 'na hakbang bago ito dumating sa iyo.'],
  waitingOnYouAreHere: ['you are here', '你在这里', 'nandito ka'],
  // The staging banner. {host} is the test Odoo's hostname.
  stagingT: ['Staging', '测试环境', 'Staging'],
  stagingBannerT: [
    'This app talks to {host}, not production. Work recorded here changes that test Odoo only.',
    '此应用连接的是 {host}，而非生产环境。此处记录的工作仅更改该测试 Odoo。',
    'Kumokonekta ang app na ito sa {host}, hindi sa production. Ang gawaing naitala rito ay nagbabago lang sa test Odoo na iyon.',
  ],
  notProductionT: ['not production', '非生产环境', 'hindi production'],
  productionAppT: ['Production app', '生产环境应用', 'Production app'],
  routeNowAt: ['Now at', '当前在', 'Nasa'],
  routeAllDone: ['Every operation is done.', '所有工序均已完成。', 'Tapos na ang lahat ng operasyon.'],
  routeNothingLive: [
    'Nothing is ready to work on yet.',
    '目前没有可开工的工序。',
    'Wala pang handang gawin.',
  ],
  routeOfDone: ['of', '/', 'sa'],
  routeOperations: ['operations done', '道工序已完成', 'na operasyon tapos na'],
  routeTapHint: [
    'Tap any step still to do to go to that work centre.',
    '点按任一未完成工序可跳转到对应工作中心。',
    'I-tap ang anumang hakbang na hindi pa tapos para pumunta sa work centre na iyon.',
  ],
  waitingOnTapHint: [
    'Tap a step to go to that work centre.',
    '点按某道工序可跳转到对应工作中心。',
    'I-tap ang hakbang para pumunta sa work centre na iyon.',
  ],
  waitingOnNothingUpstream: [
    'Everything before this station is finished, so it is not waiting on a work order. Check the manufacturing order itself — it may not be released.',
    '此工位之前的工序均已完成，因此并非在等待工单。请检查制造订单本身，可能尚未发布。',
    'Tapos na ang lahat bago ang station na ito, kaya hindi ito naghihintay ng work order. Tingnan ang manufacturing order mismo.',
  ],
  waitingOnUnknown: [
    'This pod is not on the route we can see. Ask a team leader to check the manufacturing order.',
    '在可见的工艺路线中找不到此舱体。请让组长检查制造订单。',
    'Wala ang pod na ito sa nakikitang ruta. Ipatingin sa team leader ang manufacturing order.',
  ],
  scanAimAtLabel: [
    'Point the camera at the pod label',
    '将摄像头对准舱体标签',
    'Itutok ang camera sa label ng pod',
  ],
  scanStartingCamera: ['Starting the camera…', '正在启动摄像头…', 'Binubuksan ang camera…'],
  scanTypeInstead: ['Type the number instead', '改为输入编号', 'I-type na lang ang numero'],
  scanNoCamera: [
    'This device cannot scan with its camera. Type the pod number instead.',
    '此设备无法使用摄像头扫描，请改为输入舱体编号。',
    'Hindi makapag-scan ang device na ito. I-type na lang ang numero ng pod.',
  ],
  scanNoPermission: [
    'Camera access was declined. Allow it in the browser, or type the number.',
    '摄像头权限被拒绝。请在浏览器中允许，或输入编号。',
    'Tinanggihan ang camera. Payagan ito sa browser, o i-type ang numero.',
  ],
  installTitle: ['Install on this device', '在此设备上安装', 'I-install sa device na ito'],
  installWhy: [
    'Opens full screen, without the browser bar.',
    '全屏打开，无浏览器地址栏。',
    'Bubukas nang full screen, walang browser bar.',
  ],
  installIosHow: [
    'Tap Share, then “Add to Home Screen”.',
    '点按“分享”，然后选择“添加到主屏幕”。',
    'I-tap ang Share, tapos “Add to Home Screen”.',
  ],
  installAction: ['Install', '安装', 'I-install'],
  dismiss: ['Dismiss', '关闭', 'I-dismiss'],
  installedAlready: [
    'Installed — you are running the app.',
    '已安装 — 正在以应用方式运行。',
    'Naka-install — tumatakbo ka na sa app.',
  ],
  findPodHint: [
    'Search by MO number, part code or project',
    '按制造单号、零件编码或项目搜索',
    'Hanapin sa MO number, part code o proyekto',
  ],
  yourPod: ['Your pod', '你的舱体', 'Ang pod mo'],
  queue: ['Queue', '队列', 'Pila'],
  pods: ['pods', '个舱体', 'pods'],
  report: ['Report', '报告', 'Iulat'],
  reportProblem: ['Report a problem', '报告问题', 'Mag-ulat ng problema'],
  qualityChecks: ['Quality checks', '质量检查', 'Mga quality check'],
  completeJobT: ['Complete job', '完成作业', 'Tapusin ang trabaho'],
  settings: ['Settings', '设置', 'Mga setting'],
  language: ['Language', '语言', 'Wika'],
  appearance: ['Appearance', '外观', 'Hitsura'],
  accentT: ['Accent', '强调色', 'Accent'],
  light: ['Light', '浅色', 'Maliwanag'],
  dark: ['Dark', '深色', 'Madilim'],
  systemT: ['System', '跟随系统', 'Sistema'],
  station: ['Station', '工位', 'Istasyon'],
  flowlineT: ['Flowline', '生产线', 'Flowline'],
  workcentre: ['Work centre', '工作中心', 'Work centre'],
  waiting: ['waiting', '等待', 'naghihintay'],
  myAlerts: ['My quality alerts', '我的质量警报', 'Mga quality alert ko'],
  podGraphicT: ['Pod graphic', '舱体图形', 'Graphic ng pod'],
  homeScreenT: ['Home screen', '主屏幕', 'Home screen'],
  podFocusT: ['Pod focus', '舱体聚焦', 'Pod focus'],
  shiftTimelineT: ['Shift timeline', '班次时间线', 'Shift timeline'],
  onStationNow: ['On this station now', '当前在此工位', 'Nasa station ngayon'],
  nobodyOnTheClock: ['Nobody is on the clock here', '当前无人在此工作', 'Walang nagtatrabaho dito'],
  podMark: ['Pod mark', '标志', 'Marka'],
  podPhoto: ['Photo', '照片', 'Larawan'],
  podMinimal: ['Minimal', '极简', 'Minimal'],
  logout: ['Log out', '退出登录', 'Mag-log out'],
  welcomeBack: ['Welcome back,', '欢迎回来，', 'Maligayang pagbabalik,'],
  enterPin: ['Enter your PIN', '输入 PIN', 'Ilagay ang PIN'],
  signInPin: ['Sign in with your operator PIN', '使用操作员 PIN 登录', 'Mag-sign in gamit ang operator PIN'],
  notYou: ['Not you? Switch operator', '不是你？切换操作员', 'Hindi ikaw? Magpalit ng operator'],
  back: ['Back', '返回', 'Bumalik'],
  wrongPin: ['Incorrect PIN — try again', 'PIN 错误 — 请重试', 'Maling PIN — subukan ulit'],
  enterOpPin: ['Enter operator PIN', '输入操作员 PIN', 'Ilagay ang operator PIN'],
  stationBoard: ['station board', '工位看板', 'station board'],
  signInPickPod: [
    'Sign in with your operator PIN to pick up the next pod in the queue.',
    '使用操作员 PIN 登录，领取队列中的下一个舱体。',
    'Mag-sign in gamit ang PIN para kunin ang susunod na pod sa pila.',
  ],
  podsWaiting: ['pods waiting', '个舱体等待', 'pods na naghihintay'],
  completedTodayT: ['completed today', '今日完成', 'natapos ngayon'],
  podJourney: ['Pod journey', '舱体旅程', 'Paglalakbay ng pod'],
  estimated: ['Estimated', '预计', 'Tinatantya'],
  quality: ['Quality', '质量', 'Kalidad'],
  statusT: ['Status', '状态', 'Katayuan'],
  wcBlocked: ['Work centre blocked', '工作中心已封锁', 'Naka-block ang work centre'],
  clear: ['Clear', '清除', 'I-clear'],
  onTrack: ['on track', '进度正常', 'nasa track'],
  earlierToday: [
    'Earlier today · scroll up for all',
    '今天早些时候 · 上滑查看全部',
    'Kanina · mag-scroll pataas',
  ],
  queued: ['queued', '排队中', 'naka-pila'],
  done: ['done', '完成', 'tapos'],
  pauseT: ['Pause', '暂停', 'I-pause'],
  resumeT: ['Resume', '继续', 'Ituloy'],
  pausedT: ['Paused', '已暂停', 'Naka-pause'],
  instructionsRead: [
    'Instructions — read before signing off',
    '说明 — 签核前请阅读',
    'Mga tagubilin — basahin bago mag-sign off',
  ],
  scrollToSignoff: [
    '↓ Scroll to the end of the instructions to sign off',
    '↓ 滑动到说明末尾以签核',
    '↓ Mag-scroll hanggang dulo para mag-sign off',
  ],
  pass: ['Pass', '通过', 'Pasado'],
  fail: ['Fail', '不通过', 'Bagsak'],
  record: ['Record', '记录', 'I-record'],
  tapCommon: ['or tap a common value', '或点按常用值', 'o pumili ng karaniwang halaga'],
  tapCapture: ['Tap to capture', '点按拍照', 'I-tap para kumuha'],
  photoCaptured: ['Photo captured', '已拍照', 'Nakuha ang larawan'],
  retake: ['Retake', '重拍', 'Kunan ulit'],
  confirmPhoto: ['Confirm photo', '确认照片', 'Kumpirmahin'],
  confirmChecked: ['Confirm I have checked', '确认我已检查', 'Kumpirmahing na-check ko'],
  confirmed: ['Confirmed', '已确认', 'Nakumpirma'],
  undo: ['Undo', '撤销', 'I-undo'],
  docsShort: ['Setout drawing · plans · standards', '排版图 · 图纸 · 标准', 'Setout · plano · pamantayan'],
  documents: ['Documents', '文档', 'Mga dokumento'],
  completeAdvance: ['Complete · advance pod →', '完成 · 推进舱体 →', 'Tapusin · isulong ang pod →'],
  wallComplete: ['Wall tiling complete', '墙面铺砖完成', 'Tapos na ang wall tiling'],
  thisPod: ['This pod', '此舱体', 'Ang pod na ito'],
  isReadyFor: ['is now ready for', '现已准备好进入', 'handa na para sa'],
  podMovedOn: [
    'This pod has moved on to the next work centre.',
    '此舱体已流转至下一工作中心。',
    'Lumipat na ang pod na ito sa susunod na work centre.',
  ],
  nextPodT: ['Next pod', '下一个舱体', 'Susunod na pod'],
  backToStation: ['Back to station', '返回工位', 'Bumalik sa istasyon'],
  yourWins: ['Your wins', '你的成就', 'Mga panalo mo'],
  rftEveryShift: [
    'Right first time, every shift this week. You are in the top 10% on Flowline 2.',
    '本周每班一次通过。你在 2 号生产线名列前 10%。',
    'Tama sa unang subok, bawat shift. Nasa top 10% ka sa Flowline 2.',
  ],
  recentCeleb: ['Recent celebrations', '近期庆祝', 'Kamakailang selebrasyon'],
  capturedAuto: [
    'Pod, work centre and your details are captured automatically.',
    '舱体、工作中心和你的信息将自动记录。',
    'Awtomatikong kinukuha ang pod, work centre at detalye mo.',
  ],
  qualityAlert: ['Quality alert', '质量警报', 'Quality alert'],
  safetyAlert: ['Safety alert', '安全警报', 'Safety alert'],
  blockWcBtn: ['Block this work centre', '封锁此工作中心', 'I-block ang work centre na ito'],
  blockWc: ['Block work centre', '封锁工作中心', 'I-block ang work centre'],
  product: ['Product', '产品', 'Produkto'],
  manufOrder: ['Manufacturing order', '制造订单', 'Manufacturing order'],
  raisedByL: ['Raised by', '报告人', 'Iniulat ni'],
  findProblem: ['Find the problem', '查找问题', 'Hanapin ang problema'],
  whichTeamT: ['Which team is this for?', '这是给哪个团队的？', 'Para saang team ito?'],
  chooseTeamT: ['Choose a team', '选择团队', 'Pumili ng team'],
  whatsWrongT: ['What is wrong?', '出了什么问题？', 'Ano ang mali?'],
  describeProblemT: ['Describe the problem…', '描述问题…', 'Ilarawan ang problema…'],
  subcategoriesT: ['subcategories', '个子类别', 'na subkategorya'],
  allCategoriesT: ['All categories', '全部类别', 'Lahat ng kategorya'],
  noCategoriesTeamT: [
    'This team does not use categories — say what is wrong in your own words.',
    '该团队不使用类别 — 请用自己的话描述问题。',
    'Hindi gumagamit ng kategorya ang team na ito — sabihin sa sarili mong salita kung ano ang mali.',
  ],
  closeT: ['Close', '关闭', 'Isara'],
  signedOffT: ['Signed off', '已签核', 'Na-sign off'],
  searchDefect: ['Search category or defect…', '搜索类别或缺陷…', 'Maghanap ng kategorya o depekto…'],
  noMatchingDefectT: ['No matching defect', '没有匹配的缺陷', 'Walang tugmang depekto'],
  // The way out when the taxonomy does not have it (lib/defectSearch.ts).
  notListedT: ['Not listed? Raise it as Other', '没有找到？按“其他”提交', 'Wala sa listahan? Itaas bilang Iba pa'],
  typedGoesInNoteT: ['goes in the description', '将写入描述', 'ilalagay sa paglalarawan'],
  category: ['Category', '类别', 'Kategorya'],
  subcategory: ['Subcategory', '子类别', 'Subkategorya'],
  change: ['Change', '更改', 'Palitan'],
  assignTeam: ['Assign to team', '分配团队', 'Italaga sa team'],
  priority: ['Priority', '优先级', 'Priyoridad'],
  low: ['Low', '低', 'Mababa'],
  normal: ['Normal', '普通', 'Normal'],
  high: ['High', '高', 'Mataas'],
  urgent: ['Urgent', '紧急', 'Agaran'],
  attach: ['Attach', '附件', 'Maglakip'],
  takePhoto: ['Take photo', '拍照', 'Kumuha ng larawan'],
  retakePhoto: ['Retake photo', '重拍', 'Kunan ulit'],
  removeT: ['Remove', '移除', 'Alisin'],
  voiceNote: ['Voice note', '语音备注', 'Voice note'],
  voiceConsentAsk: [
    'uses your browser’s speech service. Tap to allow.',
    '将使用浏览器的语音服务。点按以允许。',
    'gumagamit ng speech service ng browser. I-tap para payagan.',
  ],
  stopT: ['Stop', '停止', 'Itigil'],
  voiceTranscribed: ['Voice note · transcribed', '语音备注 · 已转写', 'Voice note · na-transcribe'],
  addNote: ['Add note (optional)', '添加备注（可选）', 'Magdagdag ng note'],
  describeSaw: ['Describe what you saw…', '描述你看到的情况…', 'Ilarawan ang nakita mo…'],
  raiseAlert: ['Raise alert', '提交警报', 'Mag-raise ng alert'],
  pickIssue: ['Choose a problem', '选择问题', 'Pumili ng problema'],
  newQualityAlert: ['New quality alert', '新建质量警报', 'Bagong quality alert'],
  newSafetyAlert: ['New safety alert', '新建安全警报', 'Bagong safety alert'],
  qualityRaised: ['Quality alert raised', '质量警报已提交', 'Na-raise ang quality alert'],
  safetyRaised: ['Safety alert raised', '安全警报已提交', 'Na-raise ang safety alert'],
  newTag: ['NEW', '新', 'BAGO'],
  notified: ['Notified', '已通知', 'Naabisuhan'],
  backToWork: ['Back to work', '返回工作', 'Bumalik sa trabaho'],
  backToBoard: ['Back to the board', '返回看板', 'Bumalik sa board'],
  plansDocs: ['Plans & documents', '图纸和文档', 'Mga plano at dokumento'],
  searchPodsPh: ['MO number, project or pod type…', 'MO 编号、项目或舱体类型…', 'MO number, proyekto o uri ng pod…'],
  noPodsMatch: ['No pods match', '无匹配舱体', 'Walang tugmang pod'],
  searchQueueHint: [
    'Search the queue by MO, project or pod type',
    '按 MO、项目或舱体类型搜索队列',
    'Hanapin ang pila ayon sa MO, proyekto o uri',
  ],
  thatWasQuick: ['That was quick ⏱', '这也太快了 ⏱', 'Ang bilis nyan ⏱'],
  underHalf: [
    'This is under half the expected time. Confirm your actual start time and a reason before completing.',
    '这不到预计时间的一半。完成前请确认实际开始时间和原因。',
    'Wala pa sa kalahati ng inaasahang oras. Kumpirmahin ang aktwal na start time at dahilan.',
  ],
  actualStart: ['Actual start time', '实际开始时间', 'Aktwal na start time'],
  reasonT: ['Reason', '原因', 'Dahilan'],
  confirmComplete: ['Confirm & complete', '确认并完成', 'Kumpirmahin at tapusin'],
  qualityIssuesTitle: ['Quality issues', '质量问题', 'Mga quality issue'],
  thisPodTab: ['This pod', '此舱体', 'Pod na ito'],
  myStationsT: ['My stations', '我的工位', 'Mga station ko'],
  allStationsT: ['All stations', '所有工位', 'Lahat ng station'],
  raisedByMe: ['Raised by me', '我提交的', 'Iniulat ko'],
  noOpenIssues: ['No open quality issues', '无未结质量问题', 'Walang bukas na quality issue'],
  nice: ['Nice', '不错', 'Galing'],
  pausePod: ['Pause the pod', '暂停舱体', 'I-pause ang pod'],
  chooseReasonLogged: [
    'Choose a reason — logged to Odoo against the work order.',
    '选择原因 — 将记录到 Odoo 的工单中。',
    'Pumili ng dahilan — itatala sa Odoo laban sa work order.',
  ],
  todayT: ['today', '今日', 'ngayon'],
  rightFirstTime: ['Right first time', '一次通过', 'Tama sa unang subok'],
  defectsDownstream: ['Defects downstream', '下游缺陷', 'Depekto sa downstream'],
  onFlowlineNow: ['On this flowline now', '当前生产线人员', 'Nasa flowline ngayon'],
  openIssuesThisPod: ['Open quality issues · this pod', '未结质量问题 · 此舱体', 'Bukas na quality issues · pod na ito'],
  noOpenThisPod: ['No open issues on this pod ✓', '此舱体无未结问题 ✓', 'Walang bukas na isyu ✓'],
  completedEarlier: ['Completed earlier', '早前完成', 'Natapos kanina'],
  notStarted: ['Not started', '未开始', 'Hindi pa nagsisimula'],
  synced: ['Synced', '已同步', 'Naka-sync'],
  offline: ['Offline', '离线', 'Offline'],
  dayStreakSuffix: ['-day streak', '天连胜', '-araw na streak'],
  wallsTiledReady: [
    'Walls tiled → this pod is ready for',
    '墙面已铺砖 → 此舱体已准备好进入',
    'Natiles ang dingding → handa na ang pod para sa',
  ],
  // The spec references t.rft in the timeline but never defines it — fixed here.
  rft: ['RFT ✓', '一次通过 ✓', 'RFT ✓'],

  // ── v3 additions (net-new screens and states the design never had) ──────
  // Sign-in mode chooser
  signIn: ['Sign in', '登录', 'Mag-sign in'],
  signInOperatorPin: ['Operator PIN', '操作员 PIN', 'Operator PIN'],
  signInOperatorPinSub: ['Your own employee PIN', '你自己的员工 PIN', 'Sarili mong employee PIN'],
  signInOdooAccount: ['Odoo account', 'Odoo 账户', 'Odoo account'],
  signInOdooAccountSub: ['Your own login and password', '你自己的账号和密码', 'Sarili mong login at password'],
  useDifferentSignIn: ['Use a different sign-in', '使用其他登录方式', 'Gumamit ng ibang sign-in'],
  // Credentials + TOTP
  odooLogin: ['Odoo login', 'Odoo 登录名', 'Odoo login'],
  password: ['Password', '密码', 'Password'],
  wrongLogin: ['Wrong login or password', '登录名或密码错误', 'Maling login o password'],
  twoFactor: ['Two-factor code', '两步验证码', 'Two-factor code'],
  twoFactorSub: [
    'Enter the 6-digit code from your authenticator',
    '输入验证器中的 6 位验证码',
    'Ilagay ang 6-digit code mula sa authenticator',
  ],
  trustDevice: ['Trust this device for 90 days', '90 天内信任此设备', 'Pagkatiwalaan ang device na ito ng 90 araw'],
  wrongCode: ['That code was not accepted', '验证码未被接受', 'Hindi tinanggap ang code'],
  // Roster
  chooseYourName: ['Choose your name', '选择你的姓名', 'Piliin ang pangalan mo'],
  atThisStation: ['At this station', '本工位', 'Sa istasyong ito'],
  everyone: ['Everyone', '全部人员', 'Lahat'],
  searchName: ['Search your name…', '搜索姓名…', 'Hanapin ang pangalan…'],
  // Connection + errors
  connecting: ['Connecting…', '连接中…', 'Kumokonekta…'],
  cantReachServer: ['Can’t reach the server', '无法连接服务器', 'Hindi maabot ang server'],
  retry: ['Retry', '重试', 'Subukan ulit'],
  couldntSave: [
    'Couldn’t save — check the connection and try again. Nothing was recorded.',
    '保存失败 — 请检查网络后重试。未记录任何内容。',
    'Hindi na-save — tingnan ang koneksyon at subukan ulit. Walang naitala.',
  ],
  offlineNoChanges: ['Offline — changes are paused', '离线 — 暂停更改', 'Offline — naka-pause ang pagbabago'],
  loading: ['Loading…', '加载中…', 'Naglo-load…'],
  tooManyTries: ['Too many tries — wait a moment', '尝试次数过多 — 请稍候', 'Sobrang dami nang subok — maghintay'],
  // Operator re-auth + logout
  confirmPinToContinue: ['Confirm your PIN to continue', '确认 PIN 以继续', 'Kumpirmahin ang PIN para magpatuloy'],
  switchOperator: ['Switch operator', '切换操作员', 'Magpalit ng operator'],
  signOutDevice: ['Sign out of this device', '退出此设备', 'Mag-sign out sa device na ito'],
  // Alert confirmation (real data, no fake responders)
  whatHappensNext: ['What happens next', '接下来', 'Ano ang susunod'],
  assignedTo: ['Assigned to', '已分配给', 'Nakatalaga kay'],
  dueBy: ['Due by', '截止', 'Deadline'],
  trackInMyAlerts: ['Track it in My quality alerts →', '在“我的质量警报”中跟踪 →', 'Subaybayan sa Mga quality alert ko →'],
  // Checks
  naT: ['N/A', '不适用', 'N/A'],
  checkRecordedInOdoo: [
    'This check is recorded in Odoo — confirm once done',
    '此检查记录在 Odoo 中 — 完成后确认',
    'Naitatala sa Odoo ang check na ito — kumpirmahin kapag tapos',
  ],
  chooseOne: ['Choose one', '选择一项', 'Pumili ng isa'],
  noOptionsConfiguredT: [
    'No options are set up for this check — tell a leader',
    '此检查未配置选项 — 请告知组长',
    'Walang naka-set na opsyon para sa check na ito — sabihin sa leader',
  ],
  chooseMouldT: ['Choose the mould this pod was cast in', '选择浇铸此舱体的模具', 'Piliin ang molde na pinaghulmahan ng pod'],
  noMouldRegisteredT: [
    'No mould is registered for this pod type. Register it under Manufacturing › Moulds, then complete this check.',
    '此舱体类型未登记任何模具。请先在"制造 › 模具"中登记，再完成此检查。',
    'Walang nakarehistrong molde para sa pod type na ito. Irehistro ito sa Manufacturing › Moulds, saka tapusin ang check.',
  ],
  enterValue: ['Enter value', '输入数值', 'Maglagay ng halaga'],
  saving: ['Saving…', '保存中…', 'Sine-save…'],
  // ── operator & leader home ──────────────────────────────────────────────
  // Chrome only, as everywhere else here: work centre, flowline, pod and alert
  // text arrives from Odoo as data and is never a translation key.
  homeT: ['Home', '主页', 'Home'],
  podsT: ['Pods', '舱体', 'Mga pod'],
  raiseT: ['Raise', '上报', 'Mag-report'],
  moreT: ['More', '更多', 'Higit pa'],
  sectionsT: ['Sections', '版块', 'Mga seksyon'],

  // Score
  todayAtT: ['Today at', '今天在', 'Ngayon sa'],
  todayByFlowlineT: ['Today, by flowline', '今日各流水线', 'Ngayon, bawat flowline'],
  ofTodayT: ['today', '今日目标', 'ngayon'],
  doneTodayT: ['done today', '今日已完成', 'tapos ngayon'],
  aheadT: ['Ahead', '超前', 'Nangunguna'],
  behindT: ['Behind', '落后', 'Nahuhuli'],
  onPaceT: ['On pace', '按进度', 'Nasa tamang bilis'],
  onPaceLowerT: ['on pace', '按进度', 'nasa bilis'],
  noTargetT: ['no target', '无目标', 'walang target'],
  offShiftT: ['off shift', '非当班', 'wala sa shift'],
  noTargetTodayT: [
    'No target set for today — showing what is done',
    '今日未设目标 — 仅显示已完成数',
    'Walang target ngayon — ipinapakita ang natapos',
  ],
  notWorkingTodayT: [
    'Not a working day for this station',
    '此工位今日不排班',
    'Hindi araw ng trabaho para sa station na ito',
  ],
  nothingRoutedT: ['Nothing routed here today', '今日无工单流经此处', 'Walang naka-ruta rito ngayon'],
  paceSaysT: ['Pace says', '进度应为', 'Ayon sa bilis'],
  byNowT: ['by now', '（当前）', 'sa ngayon'],
  youAreUpT: ["you're", '你', 'ikaw ay'],
  upT: ['up', '超前', 'nangunguna'],
  downT: ['down', '落后', 'nahuhuli'],
  toGoT: ['to go', '待完成', 'natitira'],

  // Starvation
  starvingT: ['Starving', '将断料', 'Nauubusan'],
  stoppedT: ['Stopped', '停机', 'Huminto'],

  // Scope
  flowlineScopeT: ['Flowline scope', '流水线范围', 'Saklaw ng flowline'],
  allFlT: ['All FL', '全部流水线', 'Lahat ng FL'],
  selectFlT: ['Select FL', '选择流水线', 'Pumili ng FL'],
  whichFlowlineT: ['Which flowline?', '哪条流水线？', 'Aling flowline?'],
  linesFeedingTodayT: [
    'The lines this station is feeding today.',
    '此工位今日供应的流水线。',
    'Ang mga linyang sinusuplayan ng station na ito ngayon.',
  ],
  allFlowlinesT: ['All flowlines', '全部流水线', 'Lahat ng flowline'],
  linesT: ['lines', '条流水线', 'na linya'],

  // Queue
  nextUpT: ['Next up', '下一个', 'Susunod'],
  inQueueT: ['in queue', '在队列中', 'nasa pila'],
  nextPodByFlowlineT: ['Next pod by flowline', '各流水线下一个舱体', 'Susunod na pod bawat flowline'],
  allTodaysPodsT: ["All today's pods", '今日全部舱体', 'Lahat ng pod ngayon'],
  thenT: ['Then', '接着', 'Pagkatapos'],
  moreCountT: ['more', '个', 'pa'],
  nothingQueuedHereT: ['Nothing queued here', '此处无排队', 'Walang nakapila rito'],
  nothingWaitingLineT: [
    'Nothing is waiting at this station on this line.',
    '此流水线上此工位没有待处理工单。',
    'Walang naghihintay sa station na ito sa linyang ito.',
  ],
  openT: ['Open', '打开', 'Buksan'],
  openPodT: ['Open pod', '打开舱体', 'Buksan ang pod'],
  checksT: ['checks', '项检查', 'na check'],
  readyT: ['Ready', '就绪', 'Handa'],
  cancelledT: ['Cancelled', '已取消', 'Kansela'],
  // The pod-list state chips (lib/podState.ts). "Not here yet" and "Signed
  // off" are shared with the rest of the app; these two are only worn there.
  inProgressT: ['In progress', '进行中', 'Isinasagawa'],
  waitingForPartsT: ['Waiting for parts', '等待物料', 'Naghihintay ng parts'],
  openRunningJobT: ['Open running job', '打开进行中的工单', 'Buksan ang tumatakbong trabaho'],
  runningT: ['Running', '进行中', 'Tumatakbo'],
  upstreamT: ['Upstream', '上游', 'Nauuna'],

  // Needs you
  needsYouT: ['Needs you', '需要你处理', 'Kailangan ka'],
  nothingWaitingOnYouT: ['Nothing waiting on you', '没有待你处理的事项', 'Walang naghihintay sa iyo'],
  nothingOpenT: ['Nothing open', '无未结事项', 'Walang bukas'],
  openCountT: ['open', '项未结', 'bukas'],
  andonsT: ['Andons', '安东', 'Mga andon'],
  noOpenAndonsT: ['No open andons', '无未结安东', 'Walang bukas na andon'],
  qualityT: ['Quality', '质量', 'Kalidad'],
  raisedByYouOpenT: ['Raised by or assigned to you, still open', '由你上报或分配给你，仍未结', 'Inireport mo o nakatalaga sa iyo, bukas pa'],
  noOpenQualityT: ['No open quality issues', '无未结质量问题', 'Walang bukas na isyu sa kalidad'],
  slaMissedT: ['SLA missed', '超出响应时限', 'Lampas sa SLA'],
  respondingT: ['responding', '正在响应', 'tumutugon'],
  onItT: ['on it', '处理中', 'inaasikaso'],
  nobodyYetT: ['Nobody yet', '暂无人认领', 'Wala pa'],
  minT: ['min', '分钟', 'min'],
  dueTodayT: ['due today', '今日到期', 'due ngayon'],
  overdueT: ['overdue', '已逾期', 'lampas na'],
  dueT: ['due', '到期', 'due'],

  // Raise
  raiseAnIssueT: ['Raise an issue', '上报问题', 'Mag-report ng isyu'],
  raiseOnThisPodT: ['Raise on this pod', '就此舱体上报', 'Mag-report sa pod na ito'],
  prefilledWithT: ['Pre-filled with', '已预填', 'Napunan na ng'],
  prefilledDefaultT: [
    'Pre-filled with your station and the pod in front of you',
    '已预填你的工位和面前的舱体',
    'Napunan na ng iyong station at ang pod sa harap mo',
  ],
  andonRowTitleT: ['Andon — I need help now', '安东 — 我现在需要帮助', 'Andon — kailangan ko ng tulong ngayon'],
  andonRowSubT: [
    'Line stopped or about to · pages WC manager + specialist',
    '产线已停或将停 · 呼叫工位主管和专员',
    'Huminto o malapit nang huminto · tumatawag sa WC manager + specialist',
  ],
  qualityRowTitleT: ['Quality issue', '质量问题', 'Isyu sa kalidad'],
  qualityRowSubT: [
    'Defect or rework on this pod · creates a QA',
    '此舱体的缺陷或返工 · 创建质量警报',
    'Depekto o rework sa pod na ito · gagawa ng QA',
  ],
  safetyRowTitleT: ['Safety issue', '安全问题', 'Isyu sa kaligtasan'],
  safetyRowSubT: [
    'Hazard or near miss · opens the WHS report',
    '危险或未遂事故 · 打开安全报告表单',
    'Panganib o near miss · bubuksan ang WHS form',
  ],
  cancelT: ['Cancel', '取消', 'Kanselahin'],
  andonHandoffT: [
    'The Andon app takes it from here — it pages the work centre manager and the specialist for the type, and holds the clock on the answer.',
    '接下来由安东应用处理 — 它会呼叫工位主管和对应专员，并对响应计时。',
    'Ang Andon app na ang bahala — tinatawagan nito ang WC manager at ang specialist, at binabantayan ang oras ng sagot.',
  ],
  raiseAndonAtT: ['Raise an andon at', '在此上报安东：', 'Mag-andon sa'],
  andonNotConfiguredT: [
    'This device has not been told where the Andon app is, so it cannot hand over to it. Raise the andon on the andon tablet at your station.',
    '此设备未配置安东应用地址，无法转交。请使用工位上的安东平板上报。',
    'Hindi alam ng device na ito kung nasaan ang Andon app. Mag-andon sa andon tablet sa station mo.',
  ],
  newTagT: ['New', '新', 'Bago'],

  // Pods tab
  todaysPodsT: ["Today's pods", '今日舱体', 'Mga pod ngayon'],
  podsAtStationT: ['Pods', '舱体', 'Mga pod'],
  finishedTodayT: ['Finished today', '今日已完成', 'Natapos ngayon'],
  finishedT: ['Finished', '已完成', 'Natapos'],
  dayTodayT: ['Today', '今天', 'Ngayon'],
  dayYesterdayT: ['Yesterday', '昨天', 'Kahapon'],
  dayTomorrowT: ['Tomorrow', '明天', 'Bukas'],
  previousDayT: ['Previous day', '前一天', 'Nakaraang araw'],
  nextDayT: ['Next day', '后一天', 'Susunod na araw'],
  pickADayT: ['Pick a day', '选择日期', 'Pumili ng araw'],
  nothingFinishedThatDayT: [
    'Nothing was finished here that day',
    '当天此处无完成项',
    'Walang natapos dito noong araw na iyon',
  ],
  dayNotHappenedYetT: ['That day has not happened yet', '那一天尚未到来', 'Hindi pa dumarating ang araw na iyon'],
  queueIsTodayOnlyT: [
    'The queue is what is standing at this station now, so it only answers for today.',
    '队列是此工位当前的实际情况，因此只反映今天。',
    'Ang pila ay kung ano ang nasa station ngayon, kaya para lang ito sa araw na ito.',
  ],
  nothingFinishedYetT: ['Nothing finished here yet today', '今日此处尚无完成项', 'Wala pang natapos dito ngayon'],
  stillToDoT: ['Still to do', '待完成', 'Gagawin pa'],
  nothingWaitingStationT: ['Nothing waiting at this station', '此工位无待处理项', 'Walang naghihintay sa station na ito'],
  rightFirstTimeShortT: ['right first time', '一次合格率', 'tama sa unang subok'],
  startedT: ['Started', '已开始', 'Nagsimula'],
  podsByFlowlineT: ['Pods by flowline', '按流水线分列的舱体', 'Mga pod ayon sa flowline'],
  noFlowlineT: ['No flowline', '无流水线', 'Walang flowline'],
  futurePodsT: ['Future pods', '后续舱体', 'Mga susunod na pod'],
  todaysNumberDoneT: [
    "Today's number is done on this line",
    '本流水线今日数量已完成',
    'Tapos na ang bilang ngayon sa linyang ito',
  ],
  beyondTodaysNumberT: ["beyond today's number", '超出今日数量', "lampas sa bilang ngayon"],
  nothingOnThisLineT: [
    'Nothing on this line here today',
    '今日此处该流水线无项目',
    'Wala sa linyang ito dito ngayon',
  ],

  // More tab
  changeStationT: ['Change station', '更换工位', 'Palitan ang station'],
  noStationSetT: ['No station set', '未设置工位', 'Walang nakatakdang station'],
  myWorkT: ['My work', '我的工作', 'Aking trabaho'],
  myTodosT: ['My todos', '我的待办', 'Aking mga gagawin'],
  myQualityIssuesT: ['My quality issues', '我的质量问题', 'Aking mga isyu sa kalidad'],
  myHistoryT: ['My history — pods I worked on', '我的记录 — 我做过的舱体', 'Aking kasaysayan — mga pod na ginawa ko'],
  bufferBoardsT: ['Buffer boards', '缓冲看板', 'Mga buffer board'],

  // Leader
  productionT: ['Production', '生产', 'Produksyon'],
  teamLeaderT: ['Team leader', '班组长', 'Team leader'],
  allAreasT: ['All areas', '全部区域', 'Lahat ng lugar'],
  allT: ['All', '全部', 'Lahat'],
  areaT: ['Area', '区域', 'Lugar'],
  rightNowT: ['right now', '当前', 'ngayon'],
  dueByNowT: ['due by now', '当前应完成', 'dapat tapos na'],
  projectedT: ['Projected', '预计', 'Inaasahan'],
  byCloseT: ['by close', '至收工', 'sa pagsasara'],
  tooEarlyProjectT: ['Too early to project the close', '现在预测收工产量为时尚早', 'Masyadong maaga para hulaan ang pagsasara'],
  behindCountT: ['behind', '落后', 'nahuhuli'],
  starvedCountT: ['starved', '断料', 'nauubusan'],
  atRiskT: ['At risk', '有风险', 'May panganib'],
  onTrackT: ['On track', '正常', 'Nasa tamang landas'],
  unacknowledgedT: ['Unacknowledged', '未确认', 'Hindi pa kinilala'],
  needsYouUnackT: ['Needs you — unacknowledged', '需要你处理 — 未确认', 'Kailangan ka — hindi pa kinilala'],
  everyAndonAnsweredT: ['Every andon has been answered', '所有安东均已响应', 'Nasagot na ang lahat ng andon'],
  allAnsweredT: ['All answered', '全部已响应', 'Lahat nasagot'],
  ackT: ['Ack', '确认', 'Kilalanin'],
  workCentresT: ['Work centres', '工位', 'Mga work centre'],
  noStationsRunningT: ['No stations running in this area today', '今日此区域无运行工位', 'Walang station na tumatakbo dito ngayon'],
  nothingRunningAreaT: ['Nothing running in this area today', '今日此区域无运行', 'Walang tumatakbo dito ngayon'],
  assignedToMeT: ['Assigned to me', '分配给我', 'Nakatalaga sa akin'],
  nothingAssignedT: ['Nothing assigned to you', '没有分配给你的事项', 'Walang nakatalaga sa iyo'],
  outputVsPaceT: ['Output today vs pace', '今日产出与进度', 'Output ngayon vs bilis'],
  onTheClockT: ['on the clock', '在岗', 'nasa trabaho'],
  andonOpenT: ['andon open', '安东未结', 'may bukas na andon'],
  starvedWordT: ['starved', '断料', 'nauubusan'],
  backToFloorT: ['Back to the floor', '返回车间', 'Balik sa palapag'],
  queueByFlowlineT: ['Queue · by flowline, priority first', '队列 · 按流水线，优先在前', 'Pila · bawat flowline, prayoridad muna'],
  queuedT: ['queued', '在队列', 'nakapila'],
  // The pile behind a station on a line (lib/outbound.ts): pods finished here
  // that the next operation has not started. A count, not a verdict.
  waitingForT: ['waiting for', '件等待', 'naghihintay sa'],
  waitingDownstreamT: ['waiting downstream', '件等待下游', 'naghihintay sa susunod'],
  inBufferT: ['in buffer', '件在缓冲区', 'nasa buffer'],

  // Docs
  openDocument: ['Open document', '打开文档', 'Buksan ang dokumento'],
  noDocuments: ['No documents for this pod yet', '此舱体暂无文档', 'Wala pang dokumento para sa pod na ito'],

  // The Plans & documents sheet. Drawings are the pod's, from its SharePoint
  // folder by way of Odoo; standard work is the station's, by work centre,
  // with its department one tap in.
  // File names, revision letters, statuses and Odoo's own "no folder linked"
  // wording are domain text and arrive as they are.
  drawingsT: ['Drawings', '图纸', 'Mga drawing'],
  drawingsNone: ['No drawings in this folder', '此文件夹中没有图纸', 'Walang drawing sa folder na ito'],
  drawingsFailed: ['Could not load the drawings', '无法加载图纸', 'Hindi ma-load ang mga drawing'],
  drawingsViewer: [
    "Each drawing opens in your device's PDF viewer — pinch to zoom.",
    '图纸会在设备的 PDF 阅读器中打开 — 双指缩放。',
    'Bubukas ang bawat drawing sa PDF viewer ng device mo — i-pinch para mag-zoom.',
  ],
  drawingsUnapproved: [
    'A sheet marked Draft or Rejected is not approved to build to. Check with your team leader first.',
    '标有“草稿”或“已拒绝”的图纸未获批准施工。请先与班组长确认。',
    'Hindi aprubado para sundin ang sheet na may Draft o Rejected. Kausapin muna ang team leader mo.',
  ],
  drawingRev: ['Rev', '版本', 'Rev'],
  drawingsItem: ['item', '项', 'item'],
  drawingsItems: ['items', '项', 'na item'],
  drawingNoStatus: ['No status', '无状态', 'Walang status'],
  iswForYouT: ['Standard work', '标准作业', 'Standard work'],
  iswFailed: [
    'Could not reach the standard-work library',
    '无法连接标准作业库',
    'Hindi maabot ang standard-work library',
  ],
  iswNoneForStation: [
    'No standard work is linked to this station yet.',
    '此工位尚未关联任何标准作业。',
    'Wala pang standard work na naka-link sa station na ito.',
  ],
  iswAllInDept: [
    'All standard work in this department',
    '本部门的所有标准作业',
    'Lahat ng standard work sa department na ito',
  ],
  iswNoStation: [
    'Choose a station to see its standard work.',
    '请选择工位以查看其标准作业。',
    'Pumili ng station para makita ang standard work nito.',
  ],

  // ── the standard-work library ──────────────────────────────────────────
  //
  // Chrome only, as everywhere else in this file. The ISW's own content — its
  // number, title, process and the card itself — is domain text that arrives
  // from the library already in the language the operator picked, and is never
  // a key here.
  iswTitle: ['Standard work', '标准作业', 'Standard work'],
  iswSearch: ['Search number, process or title', '搜索编号、工序或标题', 'Maghanap ng numero, proseso o pamagat'],
  iswSearchLabel: ['Search standard work', '搜索标准作业', 'Maghanap ng standard work'],
  iswAllDraft: [
    'Every card in this library is a draft. Work to the issued document at your cell until QA has approved it.',
    '本库中的所有卡片均为草稿。在 QA 批准之前，请按你工位上已发布的文件作业。',
    'Draft pa ang lahat ng card dito. Sundin ang naka-issue na dokumento sa cell mo hangga’t hindi pa ito inaaprubahan ng QA.',
  ],
  iswRecent: ['Recently opened', '最近打开', 'Kamakailang binuksan'],
  iswAll: ['All standard work', '所有标准作业', 'Lahat ng standard work'],
  iswResults: ['Results', '搜索结果', 'Mga resulta'],
  iswNoneTitle: ['Nothing matches that', '没有匹配项', 'Walang tumugma'],
  iswNoneBody: [
    'Search by ISW number, process or what the job is called.',
    '可按 ISW 编号、工序或作业名称搜索。',
    'Maghanap ayon sa ISW number, proseso o pangalan ng trabaho.',
  ],
  iswDraft: ['Draft', '草稿', 'Draft'],
  iswReview: ['In review', '审核中', 'Sinusuri'],
  iswApproved: ['Approved', '已批准', 'Aprubado'],
  iswCardsCount: ['cards', '张卡片', 'na card'],
  iswAllDraftShort: ['all draft', '全部为草稿', 'draft lahat'],
  iswOneMatch: ['card matches', '张卡片匹配', 'card ang tumugma'],
  iswManyMatch: ['cards match', '张卡片匹配', 'na card ang tumugma'],
  iswBuilt: ['ISW library · built', 'ISW 库 · 构建于', 'ISW library · ginawa'],
  iswSignedInAs: ['signed in as', '登录身份', 'naka-sign in bilang'],
  iswNotIssued: ['draft, not issued', '草稿，未发布', 'draft, hindi pa naka-issue'],
  iswFormats: ['Other formats', '其他格式', 'Ibang format'],
  iswMobile: ['Mobile card', '手机卡片', 'Mobile card'],
  iswMobileSub: ['What a tap on the row opens', '点按该行即可打开', 'Ang bubukas kapag na-tap ang row'],
  iswA4: ['A4 training handout', 'A4 培训讲义', 'A4 training handout'],
  iswA4Sub: ['Reading copy — for training, not the cell', '阅读版 — 用于培训，非工位使用', 'Basahin lang — para sa training, hindi sa cell'],
  iswStrip: ['Pictures-only strip', '纯图片条', 'Larawan lang na strip'],
  iswStripSub: ['Photos and step numbers, no words', '仅照片和步骤编号，无文字', 'Mga larawan at step number, walang salita'],
  iswA3: ['A3 cell card (PDF)', 'A3 工位卡（PDF）', 'A3 cell card (PDF)'],
  iswA3Sub: ["Opens in the tablet's PDF viewer", '在平板的 PDF 阅读器中打开', 'Bubukas sa PDF viewer ng tablet'],
  iswA3Note: [
    'The A3 is the laminated card that hangs at the cell. Print it from the paper copy the cell already holds rather than from a tablet.',
    'A3 是挂在工位上的过塑卡片。请使用工位已有的纸质版打印，不要从平板打印。',
    'Ang A3 ay ang laminated card na nakasabit sa cell. I-print ito mula sa papel na kopya na nasa cell, hindi mula sa tablet.',
  ],
  iswNoCardTitle: ['This card is not built yet', '此卡片尚未生成', 'Hindi pa gawa ang card na ito'],
  iswNoCardBody: [
    'The library has this record but has not rendered this format. Ask a tech. author to build it.',
    '库中有此记录，但尚未生成该格式。请让技术文件编写人员生成。',
    'Nasa library ang record na ito pero hindi pa na-render ang format na ito. Ipagawa sa tech. author.',
  ],
  iswOffline: [
    'This card has not been opened on this device, and there is no signal to fetch it.',
    '本设备尚未打开过此卡片，且当前无网络无法获取。',
    'Hindi pa nabubuksan ang card na ito sa device na ito, at walang signal para kunin ito.',
  ],

  // ── v2.10: one shell, the leader's three views, the detail sheets ────────
  overviewT: ['Overview', '总览', 'Overview'],
  buffersT: ['Buffers', '缓冲区', 'Mga buffer'],
  byBufferT: ['By buffer', '按缓冲区', 'Ayon sa buffer'],
  byFlowlineT: ['By flowline', '按流水线', 'Ayon sa flowline'],
  lowAndOosOnlyT: ['Low & out-of-seq only', '仅显示偏低和乱序', 'Mababa at wala sa pagkakasunod lang'],
  bufferLinesT: ['buffer-lines', '条缓冲线', 'buffer-line'],
  lowCountT: ['low', '偏低', 'mababa'],
  outOfSequenceT: ['out of sequence', '乱序', 'wala sa pagkakasunod'],
  outOfSeqShortT: ['out of seq', '乱序', 'wala sa seq'],
  inSeqT: ['in seq', '按序', 'nasa seq'],
  minLevelT: ['min', '最低', 'min'],
  criticalT: ['Critical', '危急', 'Kritikal'],
  lowLevelT: ['Low', '偏低', 'Mababa'],
  overT: ['Over', '过量', 'Sobra'],
  inRangeT: ['In range', '正常', 'Nasa saklaw'],
  noBufferIntoT: [
    'No buffer configured into this work centre.',
    '没有配置流向此工作中心的缓冲区。',
    'Walang buffer na naka-configure papunta sa work centre na ito.',
  ],
  buffersFeedingT: ['Buffers feeding', '供给缓冲区 →', 'Mga buffer na nagpapakain sa'],
  worstFirstSubT: [
    'worst first — stopped, then starved, then owed',
    '最严重优先 — 已停线、缺料、待处理',
    'pinakamalala muna — huminto, nagugutom, may utang',
  ],
  allLinkT: ['All', '全部', 'Lahat'],
  goToWorkCentreT: ['Go to work centre', '前往工作中心', 'Pumunta sa work centre'],
  goToT: ['Go to', '前往', 'Pumunta sa'],
  closeEllipsisT: ['Close…', '关闭…', 'Isara…'],
  starvedTagT: ['STARVED', '缺料', 'NAGUGUTOM'],
  hasNothingReadyTitleT: ['has nothing ready', '没有可开工的舱体', 'walang handa'],
  podsWaitingUpstreamT: ['pods waiting upstream', '个舱体在上游等待', 'pod na naghihintay sa itaas'],
  fedByT: ['fed by', '由…供给', 'pinapakain ng'],
  blocksSignoffT: ['blocks sign-off', '阻止签核', 'humaharang sa sign-off'],
  unansweredT: ['unanswered', '未响应', 'walang sumasagot'],
  answeredByT: ['answered by', '已由…响应', 'sinagot ni'],
  flowlinesEolTodayT: ['Flowlines · EOL QC today', '流水线 · 今日 EOL QC', 'Mga flowline · EOL QC ngayon'],
  nothingWrongT: ['nothing wrong', '一切正常', 'walang problema'],
  stoppedCountT: ['stopped', '已停线', 'huminto'],
  throughEolQcT: ['through EOL QC', '通过 EOL QC', 'lumagpas sa EOL QC'],
  onShiftNowT: ['on shift now', '人正在当班', 'naka-shift ngayon'],
  onShiftNowHeadT: ['On shift now', '正在当班', 'Naka-shift ngayon'],
  shiftFinishedTodayT: ['Finished today', '今日已下班', 'Tapos na ngayon'],
  finishedCountT: ['finished', '已下班', 'tapos na'],
  soFarT: ['so far', '目前', 'sa ngayon'],
  noShiftsTodayT: ['Nobody has clocked in today', '今日尚无人打卡', 'Wala pang nag-clock in ngayon'],
  fromAttendanceNoteT: ['From attendance (Tanda)', '来自考勤（Tanda）', 'Mula sa attendance (Tanda)'],
  tapForShiftTimesT: ['tap for times and hours', '点按查看时间和工时', 'i-tap para sa oras at haba'],
  nowT: ['now', '现在', 'ngayon'],
  fromAttendanceKickerT: ['Today · from attendance', '今日 · 来自考勤', 'Ngayon · mula sa attendance'],
  groupedByDeptT: [
    'grouped by department · tap one to drill in',
    '按部门分组 · 点按进入',
    'nakagrupo ayon sa departamento · i-tap para pumasok',
  ],
  needsYouTargetsOutputT: [
    'needs you, targets, output by hour',
    '待处理、目标、每小时产量',
    'kailangan ka, target, output kada oras',
  ],
  backToOverviewT: ['Back to overview', '返回总览', 'Bumalik sa overview'],
  queueEmptyWordT: ['Queue empty', '队列为空', 'Walang laman ang queue'],
  noWorkT: ['No work', '无工作', 'Walang trabaho'],
  behindWordT: ['Behind', '落后', 'Nahuhuli'],
  nothingRoutedLineT: ['nothing routed here on this line', '此线上没有派工到这里', 'walang naka-ruta dito sa linyang ito'],
  finishedNothingMoreT: ['finished · nothing more routed today', '已完成 · 今日无更多派工', 'tapos · wala nang naka-ruta ngayon'],
  waitingUpstreamNoneReadyT: ['waiting upstream, none ready', '在上游等待，无可开工', 'naghihintay sa itaas, walang handa'],
  needYouCountT: ['need you', '需要你', 'kailangan ka'],
  fineCountT: ['fine', '正常', 'ayos'],
  offShiftNoWorkT: ['off shift / no work', '非班次 / 无工作', 'off shift / walang trabaho'],
  moreNeedYouT: ['more need you', '个还需要你', 'pa ang kailangan ka'],
  urgencyT: ['Urgency', '紧急程度', 'Pagkaapurahan'],
  lineStoppedT: ['line stopped', '线已停', 'huminto ang linya'],
  stopsWithin2hT: ['stops within 2h', '2 小时内将停线', 'hihinto sa loob ng 2 oras'],
  needsAttentionT: ['needs attention', '需要关注', 'kailangan ng atensyon'],
  typeT: ['Type', '类型', 'Uri'],
  podT: ['Pod', '舱体', 'Pod'],
  ownerT: ['Owner', '负责人', 'May-ari'],
  slaT: ['SLA', 'SLA', 'SLA'],
  ackTargetMissedT: ['Acknowledge target missed', '未按时确认', 'Hindi naabot ang target ng pagkilala'],
  respondingTagT: ['Responding', '响应中', 'Tumutugon'],
  resolvedT: ['Resolved', '已解决', 'Nalutas'],
  openInAndonAppT: ['Open in Andon app', '在 Andon 应用中打开', 'Buksan sa Andon app'],
  ackInAndonAppT: ['Acknowledge in Andon app', '在 Andon 应用中确认', 'Kilalanin sa Andon app'],
  stageT: ['Stage', '阶段', 'Stage'],
  categoryT: ['Category', '类别', 'Kategorya'],
  signOffT: ['Sign-off', '签核', 'Sign-off'],
  blocksMoSignoffUntilClosedT: [
    'Blocks MO sign-off until closed',
    '关闭前阻止制造订单签核',
    'Humaharang sa MO sign-off hanggang maisara',
  ],
  doesNotBlockT: ['Does not block', '不阻止', 'Hindi humaharang'],
  photosT: ['Photos', '照片', 'Mga larawan'],
  addPhotoT: ['Add photo', '添加照片', 'Magdagdag ng larawan'],
  chatterT: ['Chatter', '讨论', 'Chatter'],
  sendMessagePhT: ['Send a message…', '发送消息…', 'Magpadala ng mensahe…'],
  postT: ['Post', '发布', 'I-post'],
  closeFixedT: ['Close — fixed', '关闭 — 已修复', 'Isara — naayos na'],
  openInOdooT: ['Open in Odoo', '在 Odoo 中打开', 'Buksan sa Odoo'],
  onlyRaiserOrLeaderT: [
    'Sign in to edit this alert',
    '登录后才能编辑此警报',
    'Mag-sign in para i-edit ang alert na ito',
  ],
  // Closing is the one act Odoo reserves for the team's approvers.
  closeFromOdooAccountT: [
    'To close, sign in with your Odoo account as an approver of this team',
    '如需关闭，请以该团队审批人的 Odoo 账号登录',
    'Para isara, mag-sign in gamit ang iyong Odoo account bilang approver ng team na ito',
  ],
  onlyApproverCanCloseT: [
    'Only an approver of this team can close it',
    '只有该团队的审批人可以关闭',
    'Approver lang ng team na ito ang makakapagsara',
  ],
  teamT: ['Team', '团队', 'Team'],
  nowAtT: ['Now at', '当前在', 'Nasa'],
  stageUpdatedT: ['stage updated', '阶段已更新', 'na-update ang stage'],
  categoryUpdatedT: ['category updated', '类别已更新', 'na-update ang kategorya'],
  photoAttachedToT: ['Photo attached to', '照片已附加到', 'Nakakabit na ang larawan sa'],
  signedOffRecordedT: ['signed off — recorded in Odoo at', '已签核 — 已记录在 Odoo，位于', 'na-sign off — naitala sa Odoo sa'],
  starvedWorkCentresT: ['Starved work centres', '缺料工作中心', 'Mga nagugutom na work centre'],
  nothingReadyWorkWaitingT: [
    'Nothing ready and work waiting upstream',
    '无可开工且有工作在上游等待',
    'Walang handa at may trabahong naghihintay sa itaas',
  ],
  unansweredWorstFirstT: ['Unanswered, worst first', '未响应，最严重优先', 'Walang sumasagot, pinakamalala muna'],
  andonsOpenFloorWideT: ['andons open floor-wide', '个 andon 在全车间未关闭', 'andon na bukas sa buong floor'],
  openAcrossFloorT: ['Open across the floor', '全车间未关闭', 'Bukas sa buong floor'],
  backToT: ['Back to', '返回', 'Bumalik sa'],
  stepT: ['Step', '步骤', 'Hakbang'],
  routeT: ['route', '路线', 'ruta'],
  issuesOnThisPodT: ['Issues on this pod', '此舱体的问题', 'Mga isyu sa pod na ito'],
  startWorkT: ['Start work', '开始工作', 'Simulan ang trabaho'],
  readyLowerT: ['ready', '可开工', 'handa'],
  lastAtT: ['last at', '上次在', 'huling nasa'],
  completedHereTodayT: ['Completed here today', '今日在此完成', 'Natapos dito ngayon'],
  thisSessionT: ['this session', '本次会话', 'sa session na ito'],
  shiftsTodayT: ['Shifts today', '今日班次', 'Mga shift ngayon'],
  peopleT: ['people', '人', 'tao'],
  photoOpensT: ['Photo', '照片', 'Larawan'],
  wcNotStampedT: ['No work centre on this alert', '此警报未标注工作中心', 'Walang work centre sa alert na ito'],
  checksDoneOfT: ['checks done', '项检查已完成', 'na check tapos'],
  // The Done gate on a failed check. {n} is the count; the server refuses the
  // sign-off until each failed check is passed.
  checksFailedFixT: [
    '{n} check(s) failed — fix and press Pass',
    '{n} 项检查不通过 — 整改后按“通过”',
    '{n} check ang bumagsak — ayusin at pindutin ang Pasado',
  ],
  checkFailedHintT: ['Failed — rectify, then press Pass', '不通过 — 整改后按“通过”', 'Bumagsak — ayusin, saka pindutin ang Pasado'],
  // The before / after picture buckets on a quality alert.
  beforePicturesAtLeastOneT: ['Before pictures (at least 1)', '整改前照片（至少 1 张）', 'Mga larawan bago ayusin (hindi bababa sa 1)'],
  takeBeforePictureT: ['Take a before picture', '拍一张整改前照片', 'Kumuha ng larawan bago ayusin'],
  beforeT: ['Before', '整改前', 'Bago'],
  afterT: ['After', '整改后', 'Pagkatapos'],
  otherT: ['Other', '其他', 'Iba pa'],
  moveToBeforeT: ['Move to Before', '移至整改前', 'Ilipat sa Bago'],
  moveToAfterT: ['Move to After', '移至整改后', 'Ilipat sa Pagkatapos'],
  moveToOtherT: ['Move to Other', '移至其他', 'Ilipat sa Iba pa'],
  pictureMovedT: ['picture moved', '照片已移动', 'nailipat ang larawan'],
  previousPhotoT: ['Previous photo', '上一张', 'Nakaraang larawan'],
  nextPhotoT: ['Next photo', '下一张', 'Susunod na larawan'],
  addAfterPictureToCloseT: ['Add an after picture to close', '添加整改后照片以关闭', 'Magdagdag ng larawan pagkatapos para isara'],
  addBeforeAfterPicturesToCloseT: [
    'Add before and after pictures to close',
    '添加整改前和整改后照片以关闭',
    'Magdagdag ng mga larawan bago at pagkatapos para isara',
  ],
  // {qa} is the alert number, {n} the photos that did not upload.
  raisedPhotosFailedT: [
    '{qa} raised, {n} photo(s) failed — open the alert to add them',
    '{qa} 已提交，{n} 张照片上传失败 — 打开警报补传',
    '{qa} naitaas, {n} larawan ang hindi na-upload — buksan ang alert para idagdag',
  ],
  notYourStationT: ['Not your station', '不是你的工位', 'Hindi ito ang station mo'],
  noChecksConfiguredT: [
    'No quality checks configured for this step.',
    '此工序未配置质量检查。',
    'Walang quality check na naka-configure para sa hakbang na ito.',
  ],
  nothingAtStationT: ['Nothing at this station', '此工位无舱体', 'Wala sa station na ito'],
  byT: ['by', '到', 'bago mag-'],

  // ── the andon preview (frame v2.12): five tabs, the + picker, push ───────
  andonTabT: ['Andon', 'Andon', 'Andon'],
  plusPickerTitleT: ['Scan or raise', '扫描或发起', 'Mag-scan o mag-raise'],
  plusScanSubT: ['Open the pod in front of you', '打开你面前的舱体', 'Buksan ang pod sa harap mo'],
  somebodyOnItT: ['Somebody is on it', '有人在处理', 'May tumutugon na'],
  waitingForTakerT: ['Waiting for someone to take it', '等待有人接手', 'Naghihintay ng kukuha nito'],
  notificationsT: ['Notifications', '通知', 'Mga notipikasyon'],
  nothingNewT: ['Nothing new.', '没有新消息。', 'Walang bago.'],
  thisDeviceT: ['This device', '此设备', 'Ang device na ito'],
  sendTestPushT: ['Send a test notification', '发送测试通知', 'Magpadala ng test notification'],
  sendingT: ['Sending…', '发送中…', 'Ipinapadala…'],
  askingT: ['Asking…', '请求中…', 'Hinihiling…'],
  notNowT: ['Not now', '暂不', 'Hindi ngayon'],
  turnOnT: ['Turn on', '开启', 'I-on'],
  deviceT: ['device', '台设备', 'device'],
  devicesT: ['devices', '台设备', 'mga device'],
  registeredToYouT: ['registered to you', '已注册到你名下', 'nakarehistro sa iyo'],
  noDeviceRegisteredT: [
    'The server has no device registered for you right now.',
    '服务器目前没有注册到你名下的设备。',
    'Walang device na nakarehistro sa iyo sa server ngayon.',
  ],
  pushAskTitleT: ['Turn on notifications', '开启通知', 'I-on ang mga notipikasyon'],
  pushAskBodyT: [
    'Without them an andon only reaches you when you open the app — not while it is in your pocket.',
    '不开启的话，安东只有在你打开应用时才会送达——放在口袋里时收不到。',
    'Kung wala ito, aabot lang sa iyo ang andon kapag binuksan mo ang app — hindi habang nasa bulsa mo.',
  ],
  pushBlockedTitleT: ['Notifications are blocked', '通知已被阻止', 'Naka-block ang mga notipikasyon'],
  pushBlockedInstalledBodyT: [
    'Shop Floor cannot ask again. Open your device Settings → Notifications → Shop Floor and turn them on.',
    'Shop Floor 无法再次请求。请打开设备的 设置 → 通知 → Shop Floor 并开启。',
    'Hindi na makakahiling muli ang Shop Floor. Buksan ang Settings → Notifications → Shop Floor ng device mo at i-on.',
  ],
  pushBlockedTabBodyT: [
    'Shop Floor cannot ask again. Tap the icon to the left of the address bar, then Notifications → Allow.',
    'Shop Floor 无法再次请求。点按地址栏左侧的图标，然后选择 通知 → 允许。',
    'Hindi na makakahiling muli ang Shop Floor. I-tap ang icon sa kaliwa ng address bar, pagkatapos Notifications → Allow.',
  ],
  pushUnregisteredTitleT: [
    'This device is not registered for alerts',
    '此设备未注册接收警报',
    'Hindi nakarehistro ang device na ito para sa mga alert',
  ],
  pushUnregisteredBodyT: [
    'Notifications are allowed, but the subscription did not go through. Alerts wait in the app instead.',
    '通知已允许，但订阅未成功。警报将在应用内等待。',
    'Pinapayagan ang mga notipikasyon, pero hindi natuloy ang subscription. Maghihintay na lang ang mga alert sa app.',
  ],
  pushNeedsInstallTitleT: ['Add Shop Floor to your home screen', '将 Shop Floor 添加到主屏幕', 'Idagdag ang Shop Floor sa home screen mo'],
  pushNeedsInstallBodyT: [
    'iPhone and iPad only deliver notifications to an installed app. Tap Share, then Add to Home Screen.',
    'iPhone 和 iPad 只向已安装的应用推送通知。点按“分享”，然后选择“添加到主屏幕”。',
    'Sa iPhone at iPad, naka-install na app lang ang nakakatanggap ng notipikasyon. I-tap ang Share, pagkatapos Add to Home Screen.',
  ],
  pushUnsupportedTitleT: ['This browser cannot receive alerts', '此浏览器无法接收警报', 'Hindi makakatanggap ng alert ang browser na ito'],
  pushUnsupportedBodyT: [
    'It has no support for push notifications. Alerts wait in the app, and reach you when you open it.',
    '它不支持推送通知。警报将在应用内等待，打开应用时即可看到。',
    'Wala itong suporta sa push notification. Maghihintay ang mga alert sa app, at makikita mo kapag binuksan mo ito.',
  ],
  pushOffTitleT: ['Push is not set up here', '此处未设置推送', 'Hindi naka-set up ang push dito'],
  pushOffBodyT: [
    'No push keys on this deployment, or no one signed in to send to. Alerts wait in the app instead.',
    '此部署没有推送密钥，或没有可发送的登录用户。警报将在应用内等待。',
    'Walang push key sa deployment na ito, o walang naka-sign in na mapapadalhan. Maghihintay na lang ang mga alert sa app.',
  ],
  pushOkTitleT: ['Alerts can reach this device', '警报可送达此设备', 'Makakarating ang mga alert sa device na ito'],
  pushOkBodyT: [
    'Everything this app can check is in order. Send a test to be sure your phone actually buzzes.',
    '应用能检查的项目都正常。发送一条测试，确认手机真的会震动。',
    'Maayos ang lahat ng kayang suriin ng app. Magpadala ng test para masigurong tumutunog talaga ang phone mo.',
  ],
  pushTestFailedT: ['The test could not be sent.', '测试无法发送。', 'Hindi naipadala ang test.'],
  pushTestThrottledT: [
    'One was just sent. Give it a moment before trying again.',
    '刚刚已发送一条。请稍候再试。',
    'May kapapadala lang. Maghintay sandali bago sumubok muli.',
  ],
  pushTestNoDeviceT: [
    'The server has no device registered for you. Close the app fully, open it again, and retry.',
    '服务器没有注册到你名下的设备。请完全关闭应用，重新打开后再试。',
    'Walang device na nakarehistro sa iyo sa server. Isara nang buo ang app, buksan muli, at subukan ulit.',
  ],
  pushTestRejectedT: [
    'Every registered device was rejected by the push service. Turn notifications off and on again on this device.',
    '所有已注册设备均被推送服务拒绝。请在此设备上关闭再开启通知。',
    'Tinanggihan ng push service ang bawat nakarehistrong device. I-off at i-on muli ang mga notipikasyon sa device na ito.',
  ],
  sentToT: ['Sent to', '已发送至', 'Naipadala sa'],
  pushTestSentHintT: [
    'If nothing appeared, check Do Not Disturb and your phone’s notification settings for Shop Floor.',
    '如果没有收到，请检查“勿扰模式”以及手机中 Shop Floor 的通知设置。',
    'Kung walang lumabas, tingnan ang Do Not Disturb at ang notification settings ng phone mo para sa Shop Floor.',
  ],
  updateReadyTitleT: ['A newer version is ready', '新版本已就绪', 'Handa na ang mas bagong bersyon'],
  updateReadyBodyT: [
    'Installing reloads the app — finish what you are doing first.',
    '安装将重新加载应用——请先完成手头的工作。',
    'Magre-reload ang app sa pag-install — tapusin muna ang ginagawa mo.',
  ],
  installUpdateT: ['Install', '安装', 'I-install'],

  // ── the andon raise (one sheet — frame v2.12 template L584-609) ────────
  //
  // The tile labels and routes keep the Andon app's words in AU; the question
  // texts are NOT here, because they come from the server with their chips.
  // What is sent to the server is never translated: the type key and the
  // "Not sure" answer travel in English, because the server resolves them by
  // their text.
  raiseResponseT: ['Response', '响应', 'Tugon'],
  tileMissingPartT: ['Missing part', '缺少零件', 'Kulang na parte'],
  tileQualityDefectT: ['Quality defect', '质量缺陷', 'Depekto sa kalidad'],
  tileUpstreamT: ['Waiting on another station', '等待其他工位', 'Naghihintay sa ibang station'],
  tileMachineDownT: ['Machine down', '机器故障', 'Sira ang makina'],
  tileSomethingElseT: ['Something else', '其他问题', 'Iba pa'],
  tileSafetyT: ['Safety', '安全', 'Kaligtasan'],
  tileTestT: ['Test', '测试', 'Test'],
  tileRetiredLabourT: ['Short-handed', '人手不足', 'Kulang sa tao'],
  tileRetiredDesignT: ['Drawing / BOM wrong', '图纸 / BOM 有误', 'Mali ang drawing / BOM'],
  tileRetiredInfoT: ['Need info', '需要信息', 'Kailangan ng impormasyon'],
  tileRetiredDecisionT: ['Need a call', '需要决定', 'Kailangan ng desisyon'],
  routeMissingPartT: ['Stores + Procurement', '仓库 + 采购', 'Stores + Procurement'],
  routeQualityDefectT: ['Quality', '质量部', 'Quality'],
  routeUpstreamT: ['The station that owes it', '负责提供的工位', 'Ang station na may utang nito'],
  routeMachineDownT: ['Maintenance + the machine’s technician', '维修部 + 该机器的技术员', 'Maintenance + ang technician ng makina'],
  routeSomethingElseT: ['Triage + WC Manager', '分诊 + 工作中心主管', 'Triage + WC Manager'],
  routeTestT: ['Nobody — nothing is notified', '无人——不会发出任何通知', 'Walang tao — walang aabisuhan'],
  routeSafetyT: ['Hazard or near miss · opens the WHS report', '危险或险情 · 打开 WHS 报告', 'Panganib o near miss · bubuksan ang WHS report'],
  routeRetiredT: ['Retired — raise Something else', '已停用——请发起“其他问题”', 'Hindi na ginagamit — mag-raise ng Iba pa'],
  raiseNarrowingT: ['Narrowing the options to this pod…', '正在针对此舱体缩小选项…', 'Pinapaliit ang mga opsyon para sa pod na ito…'],
  raiseOneMoreComingT: [
    'One more question is on its way — it is the one that decides who comes.',
    '还有一个问题即将到来——它决定谁来处理。',
    'May isa pang tanong na paparating — ito ang magpapasya kung sino ang darating.',
  ],
  raiseThatIsEverythingT: [
    'That is everything this one needs. Tap a line above to change an answer.',
    '所需信息已齐全。点按上方任一行可更改答案。',
    'Iyan na ang lahat ng kailangan nito. I-tap ang linya sa itaas para baguhin ang sagot.',
  ],
  raiseAnsweredT: ['answered', '已回答', 'nasagot'],
  raiseChangeAnswerT: ['Change:', '更改：', 'Baguhin:'],
  raisingT: ['Raising…', '正在上报…', 'Nagra-raise…'],
  raiseOneMoreAnswerT: ['One more answer to send', '还需回答一个问题才能发送', 'Isa pang sagot bago maipadala'],
  raiseMoreAnswersT: ['more answers to send', '个问题待回答后才能发送', 'pang sagot bago maipadala'],
  raiseWaitingLastQuestionT: ['Waiting for the last question…', '正在等待最后一个问题…', 'Hinihintay ang huling tanong…'],
  qSearchT: ['Search…', '搜索…', 'Maghanap…'],
  qNotSureT: ['Not sure', '不确定', 'Hindi sigurado'],
  qGiveTimeT: ['Give a time…', '给出时间…', 'Magbigay ng oras…'],
  qSomethingElseT: ['Something else…', '其他…', 'Iba pa…'],
  qSayWhatItIsT: ['Say what it actually is', '说明实际情况', 'Sabihin kung ano talaga ito'],
  qSendAnswerT: ['Send answer', '发送答案', 'Ipadala ang sagot'],
  qPhSubjectsT: ['Pod, MO, type or project', '舱体、制造单、类型或项目', 'Pod, MO, uri o proyekto'],
  qPhDefectsT: ['What is wrong with it', '有什么问题', 'Ano ang mali dito'],
  qPhWorkcentresT: ['Station name', '工位名称', 'Pangalan ng station'],
  qPhEquipmentT: ['Machine name or asset number', '机器名称或资产编号', 'Pangalan ng makina o asset number'],
  qPhPartsT: ['Part name or code', '零件名称或编码', 'Pangalan o code ng parte'],
  qHintIdentityT: [
    'Type the pod, MO or project — “the meriton one” works',
    '输入舱体、制造单或项目——“meriton 那个”也可以',
    'I-type ang pod, MO o proyekto — puwede ang “the meriton one”',
  ],
  qHintMinutesT: ['How many minutes?', '多少分钟？', 'Ilang minuto?'],
  qRaisedHereT: ['× raised here', '次在此上报', '× na-raise dito'],
  saUseTypedT: ['Use what I typed', '使用我输入的内容', 'Gamitin ang tinype ko'],
  saLoadingListT: ['Loading the list…', '正在加载列表…', 'Nilo-load ang listahan…'],
  saSearchingRegisterT: ['Searching the register…', '正在搜索登记册…', 'Hinahanap sa register…'],
  saNoMatchT: [
    'Nothing in the list matches. Send it anyway and we will work it out.',
    '列表中没有匹配项。照常发送，我们会处理。',
    'Walang tumugma sa listahan. Ipadala pa rin at aayusin namin.',
  ],
  sayAnythingElseT: ['Anything else worth sending?', '还有其他值得发送的内容吗？', 'May iba pa bang sulit ipadala?'],
  sayShowItT: ['Show it, if you can', '如果可以，拍下来', 'Ipakita ito, kung kaya'],
  optionalT: ['Optional', '可选', 'Opsyonal'],
  sayEquipmentPhotoT: [
    'A photo of the display and the panel is worth more than a description here — it carries the error code, the state and what has been pressed.',
    '显示屏和面板的照片比文字描述更有用——它包含错误代码、状态以及已按下的按钮。',
    'Mas mahalaga ang larawan ng display at panel kaysa paglalarawan dito — dala nito ang error code, ang estado at kung ano ang napindot.',
  ],
  sayPlaceholderT: ['Say what is actually wrong', '说明实际问题', 'Sabihin kung ano talaga ang mali'],
  sayAnotherPhotoT: ['Add another photo', '再添加一张照片', 'Magdagdag ng isa pang larawan'],
  sayPhotoFailedT: ['That photo would not attach.', '该照片无法附加。', 'Hindi naikabit ang larawan.'],
  justNowT: ['Just now', '刚刚', 'Ngayon lang'],
  raiseCannotTitleT: ['Raising is not available on this session', '此会话无法上报', 'Hindi available ang pag-raise sa session na ito'],
  raiseCannotLeadT: [
    'An andon is raised BY an employee, and this session has no employee attached to it. Sign in again; if it still says this, the account has no employee record linked to it — an Andon manager can link one.',
    '安东由员工发起，而此会话没有关联的员工。请重新登录；如果仍显示此信息，说明该账户没有关联的员工记录——安东管理员可以进行关联。',
    'Ang andon ay nira-raise NG isang empleyado, at walang empleyadong nakakabit sa session na ito. Mag-sign in muli; kung ganito pa rin, walang employee record na naka-link sa account — maaaring i-link ito ng Andon manager.',
  ],
  raiseCannotFootT: [
    'Everything else keeps working — this is the one screen that needs an employee record.',
    '其他功能均正常——只有此界面需要员工记录。',
    'Gumagana pa rin ang lahat ng iba — ito lang ang screen na nangangailangan ng employee record.',
  ],
  raiseNeedStationTitleT: ['Choose a station first', '请先选择工位', 'Pumili muna ng station'],
  raiseNeedStationLeadT: [
    'The andon has to say where you are. Pick the station you are standing at and the raise carries on.',
    '安东必须说明你的位置。选择你所在的工位，然后继续上报。',
    'Kailangang sabihin ng andon kung nasaan ka. Piliin ang station na kinatatayuan mo at magpapatuloy ang pag-raise.',
  ],
  raiseCheckingT: ['Checking who you are…', '正在确认你的身份…', 'Sinusuri kung sino ka…'],
  raisedCouldNotOpenT: [
    'Raised, but this device could not open it. Help has been called — check the Andon tab.',
    '已上报，但此设备无法打开。已呼叫帮助——请查看安东标签页。',
    'Na-raise na, pero hindi ito mabuksan ng device na ito. Tinawag na ang tulong — tingnan ang Andon tab.',
  ],

  // ── the andon thread (step 4 of the merge): the verbs, the close, the homes ──
  //
  // Lifted from the Andon app's screens, which were English only. What is
  // sent to the server stays in English — the cancel reason keys, the emoji —
  // and what the server says (an event's words, a ladder rung's note, a cause
  // class's label) is drawn as sent. Everything else a person reads here is
  // a key.

  // the verb row
  verbStoppedNowT: ['Stopped now', '现已停线', 'Huminto na ngayon'],
  verbDidItStopT: ['Did it stop?', '停线了吗？', 'Huminto ba?'],
  verbWhenWillItStopT: ['When will it stop?', '何时会停线？', 'Kailan hihinto?'],
  verbTakeItT: ['Take it', '我来处理', 'Kunin ito'],
  verbTakeOverFromT: ['Take over from', '接手自', 'Kunin mula kay'],
  themT: ['them', '对方', 'kanila'],
  verbPlusFifteenT: ['+15 min', '+15 分钟', '+15 min'],
  verbSortedT: ['Sorted', '已解决', 'Naayos na'],
  verbHandToSomeoneT: ['Hand to someone', '移交他人', 'Ipasa sa iba'],
  verbEscalateT: ['Escalate', '上报', 'I-escalate'],
  verbSortedMyselfT: ['Sorted myself', '我自己解决了', 'Naayos ko mismo'],

  // the chip on a card's face
  badgeDidItStopT: ['Did it stop?', '停线了吗？', 'Huminto ba?'],
  badgeWhenWillItStopT: ['When will it stop?', '何时会停线？', 'Kailan hihinto?'],
  badgeNoOwnerT: ['No owner', '无人认领', 'Walang may-ari'],
  badgeFixRunningOverT: ['Fix running over', '修复超时', 'Lumalampas ang pag-aayos'],
  badgeRespondingT: ['Responding', '响应中', 'Tumutugon'],
  badgeOnTheWayT: ['On the way', '正在赶来', 'Papunta na'],
  yoursT: ['Yours', '你的', 'Sa iyo'],
  agoT: ['ago', '前', 'ang nakalipas'],
  slaHeldT: ['Held', '已暂停', 'Nakahinto'],
  slaEscT: ['Esc', '升级', 'Esc'],

  // the thread
  threadT: ['Thread', '对话', 'Thread'],
  threadRaisedThisT: ['raised this', '发起了此安东', 'ang nag-raise nito'],
  threadJustNowT: ['just now', '刚刚', 'ngayon lang'],
  threadMinAgoT: ['min ago', '分钟前', 'min ang nakalipas'],
  threadSendingT: ['Sending…', '发送中…', 'Ipinapadala…'],
  threadFileStillUploadingT: ['file still uploading', '个文件仍在上传', 'file na ina-upload pa'],
  threadFilesStillUploadingT: ['files still uploading', '个文件仍在上传', 'na file na ina-upload pa'],
  threadWhatIWasAskedT: ['What I was asked', '我被问到的问题', 'Ang itinanong sa akin'],
  threadTheDecisionT: ['The decision', '处理决定', 'Ang desisyon'],
  threadReactT: ['React to this message', '对此消息做出反应', 'Mag-react sa mensaheng ito'],
  threadIncludingYouT: ['including you — tap to take yours back', '包括你——点按可撤回', 'kasama ka — i-tap para bawiin'],
  threadReactionFailedT: ['That reaction would not go through.', '该反应未能发送。', 'Hindi naipadala ang reaction.'],
  youT: ['You', '你', 'Ikaw'],
  previousT: ['Previous', '上一张', 'Nakaraan'],
  nextT: ['Next', '下一张', 'Susunod'],

  // the composer
  composerPlaceholderT: ['Message the team', '给团队发消息', 'Mag-mensahe sa team'],
  composerAddAriaT: ['Add a photo, clip or file', '添加照片、短片或文件', 'Magdagdag ng larawan, clip o file'],
  composerAddEmojiT: ['Add an emoji', '添加表情', 'Magdagdag ng emoji'],
  sendT: ['Send', '发送', 'Ipadala'],
  discardT: ['Discard', '丢弃', 'Itapon'],
  composerRecordVoiceT: ['Record a voice clip', '录制语音', 'Mag-record ng voice clip'],
  composerRecordingAudioT: ['Recording audio', '正在录音', 'Nagre-record ng audio'],
  composerListeningT: ['Listening — the words will go in the box.', '正在听写——文字将填入输入框。', 'Nakikinig — mapupunta ang mga salita sa box.'],
  composerGalleryT: ['Photo or video from the gallery', '从相册选择照片或视频', 'Larawan o video mula sa gallery'],
  composerRecordAudioT: ['Record an audio clip', '录制音频', 'Mag-record ng audio clip'],
  composerTranscribesT: ['transcribes', '转写文字', 'nagta-transcribe'],
  composerRecordVideoT: ['Record a video clip', '录制视频', 'Mag-record ng video clip'],
  composerUploadT: ['Upload a file', '上传文件', 'Mag-upload ng file'],
  composerUploadHintT: ['drawing, PDF', '图纸、PDF', 'drawing, PDF'],
  composerGifHintT: ['search', '搜索', 'maghanap'],
  composerConsentTitleT: ['Turn on transcription for voice clips', '为语音开启转写', 'I-on ang transcription para sa voice clips'],
  composerTrayFullT: ['Ten files on one message is the limit — send these first.', '一条消息最多十个文件——请先发送这些。', 'Sampung file bawat mensahe ang limit — ipadala muna ang mga ito.'],
  composerWouldNotSendT: ['That would not send.', '未能发送。', 'Hindi naipadala.'],
  composerWouldNotUploadT: ['That file would not upload.', '该文件未能上传。', 'Hindi na-upload ang file.'],
  composerAttachFailedT: ['The message went, but a file could not be added to it.', '消息已发送，但有一个文件未能附加。', 'Naipadala ang mensahe, pero hindi naidagdag ang isang file.'],
  composerOneLostT: ['One file did not finish uploading.', '有一个文件未能完成上传。', 'Hindi natapos ang upload ng isang file.'],
  composerManyLostT: ['files did not finish uploading.', '个文件未能完成上传。', 'na file ang hindi natapos i-upload.'],
  composerTrailingOneT: [
    '1 file is still uploading — it will join the message you just sent.',
    '1 个文件仍在上传——它会附加到你刚发送的消息。',
    '1 file pa ang ina-upload — sasama ito sa mensaheng kapapadala mo lang.',
  ],
  composerTrailingManyT: [
    'files are still uploading — they will join the message you just sent.',
    '个文件仍在上传——它们会附加到你刚发送的消息。',
    'na file pa ang ina-upload — sasama ang mga ito sa mensaheng kapapadala mo lang.',
  ],
  composerRetryT: ['Retry', '重试', 'Subukan muli'],
  composerSendingT: ['Sending', '发送中', 'Ipinapadala'],
  composerTapRetryT: ['failed to upload — tap to try again', '上传失败——点按重试', 'hindi na-upload — i-tap para subukan muli'],
  mentionSomeoneT: ['Mention someone', '提及某人', 'Mag-mention ng tao'],
  mentionLabelThreadT: [
    'Mention — they get this and every message after it',
    '提及——对方会收到这条及之后的所有消息',
    'Mention — matatanggap nila ito at bawat mensahe pagkatapos',
  ],
  mentionLabelCloseT: [
    'Mention — they get told, and follow this andon',
    '提及——对方会收到通知并关注此安东',
    'Mention — aabisuhan sila, at susundan ang andon na ito',
  ],
  onShiftT: ['on shift', '在班', 'naka-duty'],
  captureUnreadableT: ['That file could not be read.', '该文件无法读取。', 'Hindi mabasa ang file.'],
  captureIsT: ['is', '为', 'ay'],
  captureLimitT: ['The limit is 100 MB.', '上限为 100 MB。', 'Ang limit ay 100 MB.'],
  captureThatClipT: ['That clip', '该短片', 'Ang clip na iyon'],
  recCannotT: ['This device cannot record.', '此设备无法录制。', 'Hindi makapag-record ang device na ito.'],
  recMicRefusedT: ['Microphone access was refused.', '麦克风权限被拒绝。', 'Tinanggihan ang access sa mikropono.'],
  recCameraRefusedT: ['Camera access was refused.', '摄像头权限被拒绝。', 'Tinanggihan ang access sa camera.'],
  recStartAudioFailedT: ['Could not start recording audio.', '无法开始录音。', 'Hindi masimulan ang pag-record ng audio.'],
  recStartVideoFailedT: ['Could not start recording video.', '无法开始录像。', 'Hindi masimulan ang pag-record ng video.'],

  // the video recorder
  videoRecordingT: ['Recording video', '正在录像', 'Nagre-record ng video'],
  videoWatchItBackT: ['watch it back', '回放', 'panoorin muli'],
  videoMaxT: ['Max', '最长', 'Max'],
  videoDiscardCloseT: ['Discard and close', '丢弃并关闭', 'Itapon at isara'],
  videoFilmAgainT: ['Film it again', '重新录制', 'I-film muli'],
  videoUseClipT: ['Use this clip', '使用此短片', 'Gamitin ang clip na ito'],
  videoTorchOnT: ['Torch on', '打开手电筒', 'Buksan ang torch'],
  videoTorchOffT: ['Torch off', '关闭手电筒', 'Patayin ang torch'],
  videoStopT: ['Stop recording', '停止录制', 'Ihinto ang pag-record'],
  videoFlipT: ['Turn the camera round', '切换摄像头', 'Ibalik ang camera'],
  videoTapToStopT: [
    'Tap the square to stop. You get to watch it back before it sends.',
    '点按方块停止。发送前你可以先回放。',
    'I-tap ang parisukat para huminto. Mapapanood mo ito bago ipadala.',
  ],

  // the GIF picker
  gifAddT: ['Add a GIF', '添加 GIF', 'Magdagdag ng GIF'],
  gifSearchPhT: ['Search GIFs', '搜索 GIF', 'Maghanap ng GIF'],
  gifRatedT: ['Rated G, and it goes on the thread with your name on it.', '仅限普级内容，并会以你的名义发布到对话中。', 'Rated G, at mapupunta sa thread na may pangalan mo.'],
  gifNotAnsweringT: ['GIF search is not answering. The rest of the composer still works.', 'GIF 搜索无响应。其他功能仍可使用。', 'Hindi sumasagot ang GIF search. Gumagana pa rin ang iba.'],
  gifNothingForT: ['Nothing for', '没有找到', 'Walang resulta para sa'],
  gifTypeToSearchT: ['Type something to search.', '输入内容进行搜索。', 'Mag-type para maghanap.'],
  gifSendT: ['Send', '发送', 'Ipadala ang'],
  gifSendThisT: ['Send this GIF', '发送此 GIF', 'Ipadala ang GIF na ito'],
  gifSearchingT: ['Searching…', '搜索中…', 'Hinahanap…'],

  // the emoji picker
  emojiSayingYesT: ['Saying yes', '表示同意', 'Pagsang-ayon'],
  emojiSayingNoT: ['Saying no', '表示否定', 'Pagtanggi'],
  emojiHowItIsGoingT: ['How it is going', '进展如何', 'Kumusta na'],
  emojiTheWorkT: ['The work', '工作', 'Ang trabaho'],
  emojiTheClockT: ['The clock', '时间', 'Ang oras'],
  emojiOddsAndEndsT: ['Odds and ends', '其他', 'Iba pa'],

  // the ETA sheet
  etaWhenThereT: ['when will you be there?', '你何时能到？', 'kailan ka makakarating?'],
  etaHandItOnTitleT: ['Hand it on — take it first', '移交——请先接手', 'Ipasa — kunin muna'],
  etaWaitingLeadT: ['is waiting on this. Say the time you will actually make.', '正在等待。请说出你实际能到的时间。', 'ay naghihintay nito. Sabihin ang oras na talagang aabutin mo.'],
  etaHandingLeadT: [
    'is waiting on this and nobody has given them a time. A handover passes on that commitment, so it needs one to pass on — take it, then choose who it goes to.',
    '正在等待，且尚无人给出时间。移交会转交这一承诺，因此需要先有承诺——请先接手，再选择移交对象。',
    'ay naghihintay nito at walang nagbigay ng oras. Ipinapasa ng handover ang pangakong iyon, kaya kailangan muna nito — kunin, saka piliin kung kanino ipapasa.',
  ],
  etaAfterShiftT: ['After my shift', '我下班后', 'Pagkatapos ng shift ko'],
  etaAfterShiftLowerT: ['after my shift', '我下班后', 'pagkatapos ng shift ko'],
  etaOdooWorksOutT: ['Odoo works it out', 'Odoo 自动计算', 'Odoo ang magkukwenta'],
  etaTakingT: ['Taking it…', '正在接手…', 'Kinukuha…'],
  etaThenChooseT: ['then choose who it goes to', '然后选择移交对象', 'saka piliin kung kanino'],
  etaThereInT: ['there in', '将在', 'darating sa loob ng'],
  etaPickT: ['pick an ETA', '选择预计到达时间', 'pumili ng ETA'],
  etaTakingFootT: ['The clock moves to this number. Nothing escalates while somebody is on it.', '计时将以此为准。有人处理时不会升级。', 'Lilipat ang orasan sa numerong ito. Walang i-escalate habang may umaasikaso.'],
  etaHandingFootT: [
    'Taking it stops the clock. Whoever you hand it to inherits a time that has been given, not a wait still running.',
    '接手会停止计时。接收者继承的是已给出的时间，而非仍在进行的等待。',
    'Ang pagkuha ay nagpapahinto ng orasan. Ang pagpapasahan mo ay magmamana ng oras na naibigay na, hindi ng paghihintay na tumatakbo pa.',
  ],

  // the handover sheet
  handoverTitleT: ['Hand over', '移交', 'Ipasa'],
  handoverLeadT: ['They become the owner and you stay a follower. There is never more than one owner.', '对方成为负责人，你仍是关注者。负责人始终只有一位。', 'Sila ang magiging may-ari at mananatili kang follower. Isa lang ang may-ari.'],
  handoverSearchPhT: ['Search responders', '搜索响应人', 'Maghanap ng responder'],
  handoverReadingT: ['Reading the responder list…', '正在读取响应人列表…', 'Binabasa ang listahan ng responder…'],
  handoverNobodyMatchesT: ['Nobody matches', '没有匹配', 'Walang tumugma sa'],
  handoverNoneConfiguredT: ['No responders are configured. An Andon manager sets the Responder group.', '未配置响应人。请安东管理员设置响应人组。', 'Walang naka-configure na responder. Ang Andon manager ang nagtatakda ng Responder group.'],
  toastOwnsNowT: ['owns it now — you stay a follower', '现在是负责人——你仍是关注者', 'ang may-ari na ngayon — mananatili kang follower'],

  // the cancel sheet
  cancelSortedMyselfT: ['Sorted it myself', '我自己解决了', 'Naayos ko mismo'],
  cancelMistakeT: ['Raised by mistake', '误操作发起', 'Na-raise nang hindi sinasadya'],
  cancelDuplicateT: ['Already raised', '已有人发起', 'Na-raise na dati'],
  cancelNotAProblemT: ['Not a problem after all', '其实没有问题', 'Hindi naman pala problema'],
  cancelWrongPodT: ['Wrong pod', '选错舱体', 'Maling pod'],
  cancelLeavesBoardT: ['It leaves the board and stops the clock.', '它将从看板移除并停止计时。', 'Mawawala ito sa board at hihinto ang orasan.'],
  cancelOwnerOnWayT: ['is on the way', '正在赶来', 'ay papunta na'],
  cancelTheyWillBeToldT: ['they will be told why.', '对方会被告知原因。', 'sasabihan sila kung bakit.'],
  cancelWhyRequiredT: ['Why — required', '原因——必填', 'Bakit — kailangan'],
  cancelWhichOneT: ['Which one — optional', '哪一个——可选', 'Alin — opsyonal'],
  cancelLookingT: ['Looking for the other one…', '正在查找另一个…', 'Hinahanap ang isa pa…'],
  cancelNothingElseOpenT: ['Nothing else is open. The reason still records — the link is the part that needs another andon.', '没有其他未关闭的安东。原因仍会记录——只有关联需要另一个安东。', 'Walang ibang bukas. Maitatala pa rin ang dahilan — ang link lang ang nangangailangan ng isa pang andon.'],
  cancelNotePhT: ['Found 8 in the offcut bin, no help needed', '在边角料箱找到 8 个，无需帮助', 'Nakahanap ng 8 sa offcut bin, hindi na kailangan ng tulong'],
  cancelKeepOpenT: ['Keep it open', '保持打开', 'Panatilihing bukas'],
  cancelTheAndonT: ['Cancel the andon', '取消安东', 'Kanselahin ang andon'],
  cancellingT: ['Cancelling…', '正在取消…', 'Kinakansela…'],
  cancelledToastT: ['cancelled', '已取消', 'kinansela'],
  cancelFailedT: ['That could not be cancelled.', '无法取消。', 'Hindi makansela.'],
  cancelPostsNothingDeletedT: ['Posts as your message in the thread. Nothing is deleted.', '将以你的消息发布到对话中。不会删除任何内容。', 'Ipo-post bilang mensahe mo sa thread. Walang mabubura.'],

  // the alert screen
  alertLoadingT: ['Loading…', '加载中…', 'Nilo-load…'],
  alertCouldNotReadT: [
    'This andon could not be read from Odoo. It was still raised — help has been called.',
    '无法从 Odoo 读取此安东。它已发起——已呼叫帮助。',
    'Hindi mabasa ang andon na ito mula sa Odoo. Na-raise pa rin ito — tinawag na ang tulong.',
  ],
  alertNoConnectionT: [
    'No connection. It was raised and help has been called — this will open when the device is back on the network.',
    '无网络连接。它已发起并已呼叫帮助——设备恢复联网后将打开。',
    'Walang koneksyon. Na-raise ito at tinawag na ang tulong — bubukas ito kapag nakakonekta na ulit ang device.',
  ],
  alertDidNotGoThroughT: ['That did not go through.', '未能完成。', 'Hindi natuloy.'],
  alertSaidT: ['said', '说', 'ang nagsabi'],
  alertWhatT: ['What', '', 'Ang'],
  alertWasAskedT: ['was asked', '被问到的问题', 'ay tinanong'],
  alertAlreadyNotifiedT: ['Already notified', '已通知', 'Naabisuhan na'],
  alertNothingSaidAskT: ['Nothing said yet. Ask', '尚无对话。在走过去之前先问问', 'Wala pang sinasabi. Tanungin si'],
  alertSomethingBeforeWalkT: ['something before you walk over — they are at the station.', '——对方就在工位。', 'bago ka pumunta — nasa station sila.'],
  alertNobodyHasThisT: ['Nobody has this yet', '尚无人认领', 'Wala pang may hawak nito'],
  alertSayWhenThereT: ['say when you will be there', '说出你何时能到', 'sabihin kung kailan ka darating'],
  alertSettledT: ['Settled', '已结束', 'Tapos na'],
  alertSomebodyT: ['Somebody', '有人', 'May tao'],
  alertIsOnItT: ['is on it', '正在处理', 'ang umaasikaso'],
  alertIsComingT: ['is coming', '正在赶来', 'ay paparating'],
  alertNobodyTakenYetT: ['Nobody has taken it yet', '尚无人接手', 'Wala pang kumukuha'],
  alertHandedToT: ['Handed to', '已移交给', 'Naipasa kay'],
  alertStillNoEtaT: ['still no ETA', '尚无预计到达时间', 'wala pa ring ETA'],
  alertStopsByT: ['Stops by', '预计停线于', 'Hihinto bago mag-'],
  alertRunningOverByT: ['Running over by', '已超时', 'Lumampas nang'],
  alertFixInT: ['Fix in', '预计修复', 'Aayusin sa loob ng'],
  alertFollowingT: ['following', '人关注', 'ang sumusubaybay'],
  alertEscalatesT: ['escalates', '升级倒计时', 'ie-escalate sa'],
  detailT: ['Detail', '详情', 'Detalye'],
  alertWhoHasItT: ['Who has it', '谁在负责', 'Sino ang may hawak'],
  alertNotifiedFirstOwnsT: ['notified · first to take it owns it', '人已通知 · 先接手者负责', 'naabisuhan · unang kumuha ang may-ari'],
  alertTheStopT: ['The stop', '停线', 'Ang paghinto'],
  alertExpectedToStopAtT: ['Expected to stop at', '预计停线于', 'Inaasahang hihinto sa'],
  alertNobodyConfirmedT: ['— nobody has confirmed. It may or may not be running.', '——尚无人确认。可能仍在运行，也可能已停。', '— walang nagkumpirma. Maaaring tumatakbo pa o hindi.'],
  alertStopsInT: ['Stops in', '将于', 'Hihinto sa loob ng'],
  alertAtT: ['at', '即', 'sa'],
  alertOwnEstimateT: ['Their own estimate.', '这是本人的估计。', 'Sarili nilang tantya.'],
  alertNotSureEndOfShiftT: ['They were not sure, so this is the end of their shift.', '对方不确定，因此按其下班时间计。', 'Hindi sila sigurado, kaya ito ang katapusan ng shift nila.'],
  alertCancelThisT: ['Cancel this andon', '取消此安东', 'Kanselahin ang andon na ito'],
  alertNeedsReasonT: ['needs a reason', '需要原因', 'kailangan ng dahilan'],
  linkedQualityAlertT: ['Quality alert', '质量警报', 'Quality alert'],
  linkedAndonT: ['Andon', '安东', 'Andon'],
  escalationT: ['Escalation', '升级流程', 'Escalation'],

  // the fix estimate's units and the stop question
  hrT: ['hr', '小时', 'oras'],
  hrsT: ['hrs', '小时', 'oras'],
  stopYouSaidByT: ['You said it would stop by', '你说它将于以下时间停线：', 'Sinabi mong hihinto ito bago mag-'],
  stopLapsedT: ['Nobody has answered, so this has gone up the line to get one.', '尚无人回复，因此已向上升级以寻求答复。', 'Walang sumagot, kaya itinaas na ito para makakuha ng sagot.'],
  stopNobodyElseSeesT: ['Nobody else can see this from where they are.', '其他人从他们的位置看不到这一情况。', 'Walang ibang nakakakita nito mula sa kinalalagyan nila.'],
  stopItHasStoppedT: ['It has stopped', '已停线', 'Huminto na'],
  stopStillRunningT: ['Still running? How much longer do you reckon?', '仍在运行？你估计还能撑多久？', 'Tumatakbo pa? Gaano pa katagal sa tingin mo?'],
  stopDecidesWhoT: [
    'It decides who comes first, and we will check back with you then. A rough answer beats none — you can change it.',
    '这决定谁先来，届时我们会再确认。粗略的答案也比没有好——你可以更改。',
    'Ito ang magpapasya kung sino ang mauuna, at babalikan ka namin. Mas mabuti ang tantya kaysa wala — puwede mong baguhin.',
  ],

  // the toasts
  toastTakenOnT: ['Taken on', '已接手', 'Kinuha na'],
  toastToldT: ['told', '已通知', 'naabisuhan'],
  toastSentToT: ['sent to', '已发送给', 'ipinadala kay'],
  toastEstimateUpdatedT: ['Estimate updated to', '估计已更新为', 'Na-update ang tantya sa'],
  toastEscalatedT: ['Escalated — noted in the thread', '已升级——已记录在对话中', 'Na-escalate — nakatala sa thread'],
  toastEveryoneToldT: ['Everyone who can help has been told', '所有能帮忙的人都已收到通知', 'Naabisuhan na ang lahat ng makakatulong'],
  toastClosedT: ['Closed — that is the last of it', '已关闭——到此结束', 'Isinara na — tapos na'],
  toastQaClosedTooT: ['The quality alert is closed too', '质量警报也已关闭', 'Isinara na rin ang quality alert'],

  // the close sheet — frame v2.12 template L636-651, script L935: the class,
  // the reason, one optional line, Close <ref>
  closeTitleT: ['Sorted — write it up', '已解决——写下记录', 'Naayos na — isulat na'],
  closeClosesOnSaveT: ['Closes on save', '保存即关闭', 'Isasara sa pag-save'],
  closeWhatCausedItT: ['What caused it?', '原因是什么？', 'Ano ang sanhi?'],
  closeWhichReasonT: ['Which {class} reason?', '哪种{class}原因？', 'Aling {class} na dahilan?'],
  closeAnythingCloserT: ['Anything closer?', '有更具体的吗？', 'May mas malapit pa?'],
  closeOneLineForQualityT: ['One line for Quality · optional', '给质量部的一句话 · 可选', 'Isang linya para sa Quality · opsyonal'],
  closeWhatFixedItPhT: ['What fixed it', '怎么修好的', 'Ano ang nag-ayos'],
  closeNoteLinkedT: [
    'stays open for Quality — only the QA closes both. Quality reads the cause from here.',
    '仍由质量部处理——只有关闭 QA 才会同时关闭两者。质量部从这里读取原因。',
    'mananatiling bukas para sa Quality — ang QA lang ang magsasara ng dalawa. Dito babasahin ng Quality ang sanhi.',
  ],
  closeNoteT: [
    'Two taps, then it’s done. This is what replaces the 24 h silent close.',
    '两下点按即可完成。这取代了 24 小时静默关闭。',
    'Dalawang tap, tapos na. Ito ang pumapalit sa 24 h tahimik na pagsasara.',
  ],
  closeMoreRowT: ['More · stop and restart times', '更多 · 停线与恢复时间', 'Higit pa · oras ng paghinto at pag-restart'],
  closeNeverRequiredT: ['never required', '非必填', 'hindi kailangan'],
  closePickCauseReasonT: ['Pick a cause and a reason', '选择原因和具体原因', 'Pumili ng sanhi at dahilan'],
  closeWhatItCostT: ['What it cost', '造成的损失', 'Ang halaga nito'],
  closeNudgedStatedT: ['nudged — now stated', '已调整——现为申报值', 'inayos — ngayon ay stated'],
  closeMinATapT: ['min a tap · tap a time to pick', '分钟/次 · 点按时间可选择', 'min bawat tap · i-tap ang oras para pumili'],
  closeNothingStoppedT: ['Nothing was recorded as stopping — the line kept running at', '未记录停线——生产线持续运行，等级为', 'Walang naitalang paghinto — tumakbo pa rin ang linya sa'],
  closeStoppedT: ['Stopped', '停线', 'Huminto'],
  closeRunningT: ['Running', '恢复', 'Tumatakbo'],
  closeFiveEarlierT: ['five minutes earlier', '提前五分钟', 'limang minutong mas maaga'],
  closeFiveLaterT: ['five minutes later', '推后五分钟', 'limang minutong mas huli'],
  closeDateAndTimeT: ['— date and time', '——日期和时间', '— petsa at oras'],
  closeRunningBeforeStoppedT: ['Running is before Stopped — check the day on each.', '“恢复”早于“停线”——请检查各自的日期。', 'Mas maaga ang Tumatakbo kaysa Huminto — tingnan ang petsa ng bawat isa.'],
  closeWorkingMinutesT: ['working minutes', '工作分钟', 'minutong trabaho'],
  closeWallClockT: ['wall clock · Odoo on save', '挂钟时间 · 保存时由 Odoo 计算', 'wall clock · Odoo sa pag-save'],
  closeOdooCalendarT: ['calendar', '日历', 'calendar'],
  closeBasisMeasuredT: ['Measured', '实测', 'Nasukat'],
  closeBasisStatedT: ['Stated', '申报', 'Sinabi'],
  closeBasisNoneT: ['None', '无', 'Wala'],
  closeBasisUnknownT: ['Unknown', '未知', 'Hindi alam'],
  closeItStoppedLineT: ['It stopped the line', '导致停线', 'Pinahinto nito ang linya'],
  closeSwitchOnUpgradeT: ['Recorded as a three-star. Set the times below to what actually happened.', '记录为三星。请将下方时间设为实际情况。', 'Naitala bilang three-star. Itakda ang mga oras sa ibaba sa aktwal na nangyari.'],
  closeSwitchOffUpgradeT: [
    'Raised below three stars, so no downtime was measured. Turn this on to record one — it becomes a three-star.',
    '发起时低于三星，因此未测量停机时间。打开此项以记录——将变为三星。',
    'Na-raise sa ibaba ng three star, kaya walang nasukat na downtime. I-on ito para magtala — magiging three-star.',
  ],
  closeSwitchOnMeasuredT: ['The app measured this stop. Turn it off if the line was never actually down.', '应用测量了此次停线。如果生产线实际上从未停止，请关闭。', 'Sinukat ng app ang paghintong ito. I-off kung hindi naman talaga huminto ang linya.'],
  closeSwitchOffMeasuredT: ['No downtime saved against this one. It stays a three-star — that is who was called, and when.', '此安东不记录停机时间。仍为三星——这代表当时通知了谁、何时通知。', 'Walang downtime na itatala rito. Mananatiling three-star — iyon ang tinawag, at kailan.'],
  closeMoreCausesT: ['more — show all', '个更多——显示全部', 'pa — ipakita lahat'],
  closeCausedAtT: ['Caused at', '发生于', 'Nagmula sa'],
  closeProposedT: ['Proposed', '建议', 'Iminungkahi'],
  closeNotAStationT: ['Not a station', '非工位', 'Hindi station'],
  closeCausedAtHintT: [
    'Where it was made, not where it was found. Leave it empty if the cause was not a station — the department comes from the class above.',
    '问题产生的地点，而非发现的地点。如果原因不在工位，请留空——部门由上方类别决定。',
    'Kung saan ito ginawa, hindi kung saan natagpuan. Iwanang blangko kung hindi station ang dahilan — mula sa class sa itaas ang department.',
  ],
  closingT: ['Closing it…', '正在关闭…', 'Isinasara…'],
  closeFixTimesT: ['Running is before Stopped — fix the times', '“恢复”早于“停线”——请修正时间', 'Mas maaga ang Tumatakbo kaysa Huminto — ayusin ang mga oras'],
  closeWhereCausedT: ['Where was it caused?', '问题发生在哪里？', 'Saan ito nagmula?'],
  closeSearchStationsT: ['Search stations', '搜索工位', 'Maghanap ng station'],
  closeNotAStationLongT: ['Not a station — Planning, Procurement, IT, a supplier', '非工位——计划、采购、IT 或供应商', 'Hindi station — Planning, Procurement, IT, supplier'],
  closeWouldNotSaveT: ['That would not save.', '无法保存。', 'Hindi ma-save.'],
  closeClosedT: ['Closed', '已关闭', 'Isinara na'],
  closeOfferLeadT: ['The andon is closed. It carries', '安东已关闭。它关联了', 'Isinara na ang andon. May dala itong'],
  closeOfferTailT: ['close that too, or leave it with Quality?', '也一并关闭，还是交由质量部处理？', 'isara rin iyon, o iwan sa Quality?'],
  closeOfferHintT: [
    'The line running says nothing about whether the defect is dealt with. Only an approver for the quality team can close it, and Odoo will say so if that is not you.',
    '生产线恢复并不代表缺陷已处理。只有质量团队的审批人才能关闭，如果不是你，Odoo 会提示。',
    'Hindi sinasabi ng tumatakbong linya kung naayos na ang depekto. Tanging approver ng quality team ang makakapagsara, at sasabihin ng Odoo kung hindi ikaw iyon.',
  ],
  tooT: ['too', '也关闭', 'din'],
  closeLeaveWithQualityT: ['Leave it with Quality', '交由质量部处理', 'Iwan sa Quality'],
  closeQaFailedT: ['The quality alert could not be closed.', '质量警报无法关闭。', 'Hindi maisara ang quality alert.'],

  // ── frame v2.12 fidelity: the floating +, its pills, and the bar ─────────
  //
  // The frame's own labels (template L695-705, `fabItems`): the pill says
  // "Scan pod" where the scanner's own sheet says "Scan a pod", because the
  // pill is a verb on a dial and the sheet is a title.
  fabSafetyT: ['Safety', '安全', 'Kaligtasan'],
  fabQualityAlertT: ['Quality alert', '质量警报', 'Quality alert'],
  fabAndonT: ['Andon', '安东', 'Andon'],
  fabScanPodT: ['Scan pod', '扫描舱体', 'Mag-scan ng pod'],
  // The Andon and Quality tabs (frame script L939, L954): the title's
  // sub-line and the operator's one group.
  openOnScopeT: ['Open on', '未关闭 —', 'Bukas sa'],
  openAtWcT: ['Open at', '未关闭 — 位于', 'Bukas sa'],
  andonsAtYourWcT: ['At your work centre — reply in the thread', '在你的工作中心 — 在对话中回复', 'Sa iyong work centre — sumagot sa thread'],
  // The same group where the tap opens the read-only sheet, not a thread.
  andonsAtYourWcReadT: ['At your work centre — tap to read', '在你的工作中心 — 点按查看', 'Sa iyong work centre — i-tap para basahin'],
  replyT: ['Reply', '回复', 'Sumagot'],
  // The thread's header (frame script L918 `meta`): how bad, who owns it,
  // who raised it, and how many times this has happened here.
  metaStoppedT: ['stopped', '已停线', 'huminto'],
  metaWillStopSoonT: ['will stop soon', '即将停线', 'malapit nang huminto'],
  metaCanWorkAroundT: ['can work around', '可暂时绕过', 'may paraan muna'],
  ownsT: ['owns', '负责', 'ang may hawak'],
  raisedT: ['raised', '上报', 'nag-raise'],
  minOverT: ['min over', '分钟超时', 'min lampas'],
  nthTimeHereT: ['{n} time here', '此处第 {n} 次', 'Ika-{n} beses dito'],
  // The raise sheet (frame template L584-609, script L930). What is sent to
  // the server is never translated: "Don't know" travels as `Not sure`.
  raiseHowUrgentT: ['How urgent?', '有多紧急？', 'Gaano ka-urgent?'],
  raiseLineStoppedQT: ['Is your line stopped right now?', '你的产线现在停了吗？', 'Huminto ba ang linya mo ngayon?'],
  sevRowStoppedT: ['Line is stopped', '产线已停', 'Huminto ang linya'],
  sevRowWillStopT: ['Will stop soon', '即将停线', 'Hihinto na'],
  sevRowAttentionT: ['Needs attention', '需要关注', 'Kailangan ng atensyon'],
  raiseWhatsWrongT: ['What’s wrong?', '出了什么问题？', 'Ano ang problema?'],
  raiseFewWordsOptionalT: ['A few words · optional', '几句话 · 可选', 'Ilang salita · opsyonal'],
  raiseAttachedNoteT: [
    'Station, pod and line are attached for you. The title is generated from these answers; nothing else is.',
    '工位、舱体和产线会自动附上。标题由这些回答生成，别无其他。',
    'Nakakabit na ang station, pod at linya para sa iyo. Mula sa mga sagot na ito ang pamagat; wala nang iba.',
  ],
  raiseSendToT: ['Send ·', '发送 ·', 'Ipadala ·'],
  raisePickHowUrgentT: ['Pick how urgent', '选择紧急程度', 'Piliin kung gaano ka-urgent'],
  raisePickWhatsWrongT: ['Pick what’s wrong', '选择问题类型', 'Piliin kung ano ang problema'],
  qSkipT: ['Skip', '跳过', 'Laktawan'],
  // The operator home's "Open at this work centre" rows (frame script L862).
  homeRaisedByYouT: ['raised by you', '由你上报', 'inireport mo'],
  // The quality sheet's linked pair (frame script L919, L932).
  raiseAndonOnThisT: ['Raise an andon on this', '就此发起安东', 'Mag-raise ng andon dito'],
  closedStateT: ['Closed', '已关闭', 'Sarado'],
  // More, with the preview on (frame template L393-401, script L910).
  morePickWorkCentreT: ['Pick work centre', '选择工作中心', 'Pumili ng work centre'],
  moreWhoWorkedT: ['Who worked a pod today', '今天谁做过舱体', 'Sino ang gumawa ng pod ngayon'],
  moreCrewSubT: ['people · start and finish times', '人 · 开始与结束时间', 'tao · oras ng simula at tapos'],
  moreLibraryT: ['Standard work library', '标准作业库', 'Standard work library'],
  moreLibrarySubT: ['ISW cards', 'ISW 卡片', 'Mga ISW card'],
  moreBufferSubT: ['Opens Odoo kiosk boards in a tab', '在新标签页打开 Odoo 看板', 'Bubuksan ang Odoo kiosk boards sa isang tab'],
  moreVersionT: ['Version', '版本', 'Bersyon'],
  moreAndonPreviewT: ['andon preview on', '安东预览已开启', 'naka-on ang andon preview'],
  qDontKnowT: ['Don’t know', '不知道', 'Hindi alam'],

  // the operator home
  homeOpenAtWorkCentreT: ['Open at this work centre', '本工作中心未关闭', 'Bukas sa work centre na ito'],
  homeSortedHereTodayT: ['Sorted here today', '今日已在此解决', 'Naayos dito ngayong araw'],
  homeSortedByT: ['Sorted by', '解决人：', 'Inayos ni'],
} as const satisfies Record<string, readonly [string, string, string]>;

export type StringKey = keyof typeof TR;
export type Translations = Record<StringKey, string>;

/** Flatten the map for one language. */
export function translations(lang: Lang): Translations {
  const idx = LANGS.indexOf(lang);
  const i = idx < 0 ? 0 : idx;
  const out = {} as Translations;
  for (const key of Object.keys(TR) as StringKey[]) {
    out[key] = TR[key][i] ?? TR[key][0];
  }
  return out;
}

/** Inline interpolated strings (the spec's L({AU,中文,Fil}) idiom). */
export function L(lang: Lang, o: { AU: string; 中文?: string; Fil?: string }): string {
  return o[lang] ?? o.AU;
}

/** Speech-recognition locale; Filipino has no mapping, so it falls back. */
export function speechLocale(lang: Lang): string {
  return lang === '中文' ? 'zh-CN' : 'en-AU';
}
